// Battlemap — the active campaign as a battlefield.
//
// /admin/battlemap, an OPERATOR tool: it reads the active campaign, so
// it lives with the other surfaces that do rather than with the player
// pages. Admin-only, like the Commander and Campaign health views.
//
// ONE CHANNEL PER MEANING. This is the rule the whole surface is built
// on, and it is why each change here has been a small one. A piece
// carries six independent facts and no two share a channel:
//
//   position (x,z)  stability + which band — derived from the ticket
//                   key ALONE, so editing the commitment never
//                   reshuffles the board
//   body colour     which KIND of work: goal (gold), committed (grey),
//                   unplanned (orange, also the colour of its lines)
//   depth           progress toward done: a piece LOSES HORNS as it advances
//                   through In Progress -> Review -> Testing ->
//                   Acceptance Testing, four blows before the killing
//                   one. Measured: one piece is 8 screen px wide and
//                   the closest two are 3.5px apart, so an indicator
//                   beside a piece would overlap its neighbours —
//                   progress has to be read in the silhouette.
//   shape           open (a block) or closed (a headstone)
//   a standing mark nothing (To Do) / a sword (engaged) / a shield
//                   (blocked). Blocked is NOT a kind of work and NOT a
//                   point on the road to done, so it could not borrow
//                   either channel without lying about something else.
//   size            boss minions are larger, a cue that survives a
//                   colour-blind read
//
// Bands, far to near: boss keep, goal encampments (fenced, not raised —
// a platform with height hides the bottom of anything sunk into it),
// the field, reinforcements (arrived mid-sprint, never promised), and
// the graveyard (closed and unpromised, in rows because scattered
// markers read as debris).
//
// LINES say two things at once and no more: COLOUR is whether the work
// was committed, SOLID-vs-DOTTED is whether the hero is accountable for
// it or merely helping. The same relationship read from either end —
// click a hero, or click their ticket.
//
// Flow analytics deliberately do NOT live here; they are on Campaign
// health, where a trend line is not competing with a battlefield for
// attention. See ADR-0027.

import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api } from '../../lib/api';
import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import { createBasemapTexture } from '../battlemap/basemap';
import {
  layoutBattlefield,
  PLANE_Z_NEAR,
  PLANE_Z_FAR,
  PLANE_X_HALF,
  PLATFORM_TOP,
  type LayoutInput,
} from '../battlemap/layout';
import {
  layoutInputAt,
  hourBuckets,
  stepInstants,
  quietGaps,
  nextInstant,
  prevInstant,
  type BattlemapTimeline,
} from '../battlemap/replay';
import { ReplayRail } from './ReplayRail';
import { buildHeroGeometry, HERO_SIZE } from '../battlemap/hero-figure';
import { heroColourFor, HERO_FALLBACK } from '../battlemap/hero-colour';
import { buildBeastGeometry } from '../battlemap/beast';
import {
  advancePlayback, indexAt, instantAtIndex, pingsBetween, pingStream, closingInstants,
  isAlarm, type PingKind,
  PLAYBACK_SPEEDS, DEFAULT_SPEED,
} from '../battlemap/playback';

/** Round down to the LOCAL hour, so the rail's buckets line up with the
 *  operator's wall clock rather than the window's arbitrary start. */
function alignToLocalHour(ms: number): number {
  const d = new Date(ms);
  d.setMinutes(0, 0, 0);
  return d.getTime();
}

const fmtInstant = (ms: number) =>
  new Date(ms).toLocaleString(undefined, {
    weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit',
  });
const fmtDay = (ms: number) =>
  new Date(ms).toLocaleDateString(undefined, { day: 'numeric', month: 'short' });

// ---------------------------------------------------------------------
// Live payload (GET /api/admin/battlemap)
// ---------------------------------------------------------------------
interface BattlemapPayload {
  team_id: string;
  adventure: {
    id: string; name: string; place: string | null;
    starts_at: string; ends_at: string;
    committed: string[];
    cleared: string[];
    goals: Array<{ id: string; name: string; keys: string[] }>;
    side_quests: string[];
  } | null;
  bosses: Array<{ id: string; name: string; hp: number; damage: number }>;
  owners: Array<{ id: string; handle: string; name: string | null; color: string | null; is_bot?: boolean }>;
  owner_of: Record<string, string>;
  last_touch: Record<string, string>;
  minion_of: Record<string, string>;
  automation?: { xp: number; total_xp: number; pct: number; actors: number };
  /** Per-ticket attributes for the click-a-piece view. */
  ticket_info?: Record<string, { status: string; summary: string | null; type: string | null }>;
  /** Who the ticket is assigned to, where known. */
  assignee_of?: Record<string, string>;
  /** Everyone who moved or commented on it. */
  actors_of?: Record<string, string[]>;
  /** Still-open work that arrived mid-sprint and was never committed. */
  reinforcements?: string[];
}
interface Team { id: string; name: string; slug: string }

function toLayoutInput(d: BattlemapPayload): LayoutInput {
  const a = d.adventure;
  return {
    committed: a?.committed ?? [],
    cleared: a?.cleared ?? [],
    goals: a?.goals ?? [],
    bosses: d.bosses,
    sideQuests: a?.side_quests ?? [],
    reinforcements: d.reinforcements ?? [],
    ownerOf: d.owner_of,
    assigneeOf: d.assignee_of ?? {},
    actorsOf: d.actors_of ?? {},
    statusOf: Object.fromEntries(
      Object.entries(d.ticket_info ?? {}).map(([k, v]) => [k, v.status]),
    ),
    minionOf: d.minion_of,
    lastTouchOf: d.last_touch,
    // No campaign → a degenerate window so the elapsed number is a
    // sane 0 instead of dividing by zero.
    startsAt: a?.starts_at ?? new Date().toISOString(),
    endsAt: a?.ends_at ?? new Date(Date.now() + 1).toISOString(),
  };
}

// ---------------------------------------------------------------------
// Scene
// ---------------------------------------------------------------------
/** Assisting lines are desaturated rather than a dimmed hero colour:
 *  at a glance, "not mine" should not look like "mine, fainter". */
const ASSIST_COLOUR = '#78818f';

/** Lines to work that was never promised are a different colour from
 *  lines to the promise. Two channels, two meanings and no overlap:
 *  COLOUR says whether the work was committed, SOLID-vs-DOTTED says
 *  whether the hero is accountable for it or merely helping.
 *
 *  Orange, and deliberately a REDDER, more saturated orange than the
 *  goal gold beside it — a muted second warm colour would read as a
 *  goal, and this is the opposite of a goal.
 *
 *  The pair is set well apart in both lightness and saturation: the
 *  previous teal pair was close enough to be hard to tell apart, and
 *  the bright one sat at almost exactly the luminance of the
 *  committed-assisting slate, so a solid unplanned line and a dotted
 *  committed cloud looked similarly loud. */
export const UNPLANNED_SOLID_COLOUR = '#e8721a';
export const UNPLANNED_ASSIST_COLOUR = '#8f4a17';

/** Which colour a line should be.
 *
 *  Pure so the rule can be checked without a renderer, because getting
 *  it backwards would quietly tell the operator that unplanned work was
 *  part of the promise — the one thing this map must never do. */
export function tetherColour(
  relationship: 'assigned' | 'assisting',
  unplanned: boolean,
  heroColour: string,
): string {
  if (unplanned) {
    return relationship === 'assigned' ? UNPLANNED_SOLID_COLOUR : UNPLANNED_ASSIST_COLOUR;
  }
  return relationship === 'assigned' ? heroColour : ASSIST_COLOUR;
}

/** Most helping lines drawn at once, in either direction.
 *  A heavily-discussed ticket can carry a dozen names, and a fan of dim
 *  lines buries the single solid one that matters.
 *  ponytail: hard cap, ranked by nothing in particular. */
const ASSIST_CAP = 14;

const MOB_SIZE = 1.1;
const CLEARED_SIZE = 0.9;

// ---------------------------------------------------------------------
// Shared scene assets
// ---------------------------------------------------------------------
//
// Every material, geometry and texture the board draws with is the same
// on every scene build, so building them per rebuild is pure waste - and
// worse than waste.
//
// Measured: a rebuild cost 184ms when these were created and disposed each
// time, and 30ms when they survive. The 150ms difference was shader
// COMPILATION: disposing a material releases its compiled program, so the
// next build recompiles every one on its first render. Sixteen materials
// and geometries, recompiled on every rebuild, at 4 rebuilds a second.
//
// They live for the life of the module and are never disposed, which is
// correct rather than lazy: the GPU context goes away with the page, and
// there is exactly one board open at a time. The two parameterised cases -
// a boss's HP tier and a hero's colour - are keyed by their parameter so
// they share as well.
const sharedAssets = new Map<string, unknown>();

function shared<T extends { dispose: () => void }>(key: string, make: () => T): T {
  let v = sharedAssets.get(key) as T | undefined;
  if (!v) {
    v = make();
    sharedAssets.set(key, v);
  }
  return v;
}
const BOSS_SIZE = 3.2;
/** A closed piece is a HEADSTONE, not a block: thin, upright, and the
 *  same silhouette whether the work was promised or not. Shape says
 *  open-or-closed; colour still says which kind of work it was, so the
 *  two channels stay independent.
 *
 *  Matches the tombstone the vanquished boss cards already use, so
 *  "defeated" looks the same wherever it appears in the product. */
const TOMB_W = 0.52;
const TOMB_H = 0.92;
const TOMB_D = 0.16;

// ---------------------------------------------------------------------
// Combat animation (Phase 2a)
//
// The map used to be a snapshot: it redrew on a 15s poll and nothing
// ever moved between renders, so a sprint in full flight looked
// static. Now events land on the piece they concern.
//
// Two kinds only, deliberately:
//   strike — the ticket moved. The mob flinches; it survives.
//   kill   — the ticket closed. The mob is cut down and becomes
//            rubble.
//
// Nothing else animates. Ambient motion on a board you leave open
// for hours is noise, and the whole point of this view is that change
// is meaningful because it's rare.
// ---------------------------------------------------------------------
type AnimKind = 'strike' | 'kill';

const ANIM_MS: Record<AnimKind, number> = { strike: 700, kill: 950 };

/** Which animation an event earns, from its type and payload. */
export function kindFor(type: string, payload?: Record<string, unknown>): AnimKind {
  if (type === 'issue.transition') {
    const to = (payload?.transition as { to?: string } | undefined)?.to;
    // Terminal statuses close the ticket. The list is configurable
    // per team, but for a visual cue the common two are enough — a
    // wrong guess costs a flinch instead of a kill, not a bad number.
    if (to === 'Done' || to === 'Cancelled' || to === 'Closed' || to === 'Resolved') return 'kill';
  }
  return 'strike';
}

/**
 * Has this ticket gone quiet?
 *
 * Staleness is "no status movement in N days". A comment doesn't
 * count — engagement isn't progress, and counting it would hide
 * exactly the tickets this marker exists to surface.
 *
 * A ticket with NO movement at all this sprint is the most stale
 * thing on the board, not the least: it was committed and never
 * started.
 */
export const DEFAULT_STALE_DAYS = 3;

/** Milliseconds for one full march of the engagement dots, hero to
 *  ticket. Slowed from 1800 on request — at that speed the dots read as
 *  a flicker rather than as travel. Tunable from the URL while judging
 *  the feel: ?dot=5000 is a slow drift, ?dot=1200 is brisk. */
export const DEFAULT_DOT_CYCLE_MS = 3600;

/** Assistance dot diameter, in CSS pixels.
 *
 *  NOT multiplied by the pixel ratio. Measured: at devicePixelRatio 1 a
 *  size of 5 renders ~4 CSS px, and at devicePixelRatio 2 a size of 5
 *  ALSO renders ~4 CSS px - so three already accounts for the ratio, and
 *  multiplying by it again made every dot twice as big on a HiDPI
 *  screen. Which is what the operator saw.
 *
 *  Tunable from the URL while judging the feel: ?dotpx=3. */
export const DEFAULT_DOT_PX = 4;

export function isStale(
  lastTouchAt: string | null,
  now: number,
  staleDays: number = DEFAULT_STALE_DAYS,
): boolean {
  const age = lastTouchAt === null ? Infinity : now - new Date(lastTouchAt).getTime();
  return age > staleDays * 86_400_000;
}

export function staleDaysFor(lastTouchAt: string | null, now: number): number {
  if (lastTouchAt === null) return Infinity;
  return (now - new Date(lastTouchAt).getTime()) / 86_400_000;
}

/** Move the selection ring under a piece, or hide it.
 *
 *  Split out from the scene build so the same code positions the ring
 *  when the scene is rebuilt and when the selection changes. Two copies
 *  would drift, and the failure mode is a ring stuck under a piece that
 *  is no longer selected.
 */
export function placeSelectionRing(
  ring: THREE.Mesh | null,
  mobs: Array<{ key: string; cleared: boolean; x: number; z: number; y: number; groundY: number }>,
  key: string | null,
): void {
  if (!ring) return;
  const mob = key === null ? undefined : mobs.find((m) => m.key === key);
  // Cleared pieces are excluded as well as missing ones: a ring around
  // rubble would say "this is selected" about something that is over.
  if (!mob || mob.cleared) {
    ring.visible = false;
    return;
  }
  ring.position.set(mob.x, mob.groundY + 0.05 + mob.y, mob.z);
  ring.visible = true;
}

/** Everything the click-a-piece view says about one ticket. */
export interface TicketFacts {
  key: string;
  summary: string | null;
  type: string | null;
  /** Current column, or null when we have no transition for it yet. */
  status: string | null;
  /** Who it is assigned to, where the payload knew. Null is "unassigned"
   *  and is NOT the same as unknown. */
  assigneeId: string | null;
  assigneeName: string | null;
  /** The most recent ACTOR — who last touched it. Called out separately
   *  from the assignee because they are different facts, and the whole
   *  point of the engagement lines is telling them apart. */
  actorId: string | null;
  actorName: string | null;
  actorIsBot: boolean;
  goalName: string | null;
  minionBossName: string | null;
  cleared: boolean;
  /** null means the ticket has never moved at all, which is different
   *  from having moved a long time ago. */
  daysSinceMove: number | null;
  stale: boolean;
}

export function ticketFacts(args: {
  key: string;
  mob: { cleared: boolean; goalId: string | null; minionBossId: string | null; lastTouchAt: string | null } | null;
  info: { status: string; summary: string | null; type: string | null } | null;
  assigneeId: string | null;
  assigneeName: string | null;
  actorId: string | null;
  actorName: string | null;
  actorIsBot: boolean;
  goalName: string | null;
  minionBossName: string | null;
  now: number;
  staleDays: number;
}): TicketFacts {
  const { key, mob, info, now, staleDays } = args;
  const neverMoved = !mob || mob.lastTouchAt === null;
  const days = mob && mob.lastTouchAt !== null ? staleDaysFor(mob.lastTouchAt, now) : null;
  return {
    key,
    summary: info?.summary ?? null,
    type: info?.type ?? null,
    status: info?.status ?? null,
    assigneeId: args.assigneeId,
    assigneeName: args.assigneeName,
    actorId: args.actorId,
    actorName: args.actorName,
    actorIsBot: args.actorIsBot,
    goalName: args.goalName,
    minionBossName: args.minionBossName,
    cleared: mob?.cleared ?? false,
    daysSinceMove: days,
    // A ticket committed and never touched is the most stale thing on
    // the board, not the least — same rule the footer count uses.
    stale: neverMoved || isStale(mob.lastTouchAt, now, staleDays),
  };
}

/** The two ends of a hero-to-ticket engagement line.
 *
 *  Both ends used to sit near the GROUND — the cone's base and 0.12
 *  above whatever surface the piece stood on. A goal mob stands on a
 *  raised platform, so the line dipped below the platform's edge and
 *  vanished behind it, which is precisely the thing the tether exists
 *  to show.
 *
 *  Now: the TIP of the cone down to the CENTRE of the block. Both ends
 *  clear the platforms, and the line terminates ON the piece rather
 *  than underneath it.
 *
 *  Pure and THREE-free so it can be checked without a renderer.
 */
export interface Anchor { x: number; y: number; z: number }

/** Local half-height of the hero cone. ConeGeometry is centred on the
 *  origin, so its tip is half a height above the mesh position. */
const HERO_HALF_HEIGHT = HERO_SIZE * 1.2;

/** Which standing tickets a hero is accountable for, and which they
 *  are merely helping with.
 *
 *  Accountable means ASSIGNED TO, falling back to the most recent actor
 *  when no assignee was ever captured. That fallback is deliberate and
 *  matches what reward attribution (ADR-0026) and the campaign health
 *  load view already do, so "who owns this" means one thing product-wide
 *  — and it keeps tickets from before the assignee was recorded from
 *  being mislabelled as somebody else's assistance.
 *
 *  Assigned wins over acted. A hero who is both assigned to a ticket and
 *  the last person to touch it is accountable, not their own assistant,
 *  and a second dimmer line to the same block would just read as noise.
 */
export interface TetherPlan {
  /** Keys the hero is accountable for. */
  ownership: string[];
  /** Keys the hero worked on but is not accountable for. */
  assistance: string[];
}

export function tetherPlan(
  heroId: string,
  keys: string[],
  assigneeOf: Record<string, string>,
  actorsOf: Record<string, string[]>,
): TetherPlan {
  const ownership: string[] = [];
  const assistance: string[] = [];
  for (const k of keys) {
    // Assigned to, and nothing else. No fallback to the last actor: on an
  // unassigned ticket that is a guess, not ownership (see layout.ts).
  const accountable = assigneeOf[k];
    if (accountable === heroId) {
      ownership.push(k);
    } else if ((actorsOf[k] ?? []).includes(heroId)) {
      assistance.push(k);
    }
  }
  return { ownership, assistance };
}

/** The same distinction read from the OTHER end: given a ticket, who is
 *  accountable for it and who has merely helped.
 *
 *  Mirror image of tetherPlan, and it has to agree with it. Selecting a
 *  hero and selecting one of that hero's tickets must draw the same
 *  lines for the same reason - one solid to whoever is accountable, one
 *  dotted to everyone else who touched it - or the two views would tell
 *  different stories about the same relationship.
 */
export interface TicketTetherPlan {
  /** The hero accountable for it, or null if nobody maps to a user. */
  accountable: string | null;
  /** Everyone else who moved or commented on it. */
  assisting: string[];
}

export function ticketTetherPlan(
  key: string,
  assigneeOf: Record<string, string>,
  actorsOf: Record<string, string[]>,
): TicketTetherPlan {
  // Assigned to, and nothing else — see tetherPlan.
  const accountable = assigneeOf[key] ?? null;
  const assisting = (actorsOf[key] ?? []).filter((id) => id !== accountable);
  return { accountable, assisting };
}

/** Evenly spaced dots along an engagement line, offset by `phase` so
 *  they march from hero to ticket.
 *
 *  `t` is kept inside [0,1) with a modulo rather than being clamped, so
 *  a dot leaving the ticket end re-enters at the hero end. Clamping
 *  would pile dots up at the destination and read as stalling.
 */
export function dotPositions(
  from: Anchor,
  to: Anchor,
  count: number,
  phase: number,
  out: Float32Array,
): void {
  const step = 1 / count;
  const p = ((phase % 1) + 1) % 1;
  for (let i = 0; i < count; i += 1) {
    const t = (i * step + p) % 1;
    out[i * 3] = from.x + (to.x - from.x) * t;
    out[i * 3 + 1] = from.y + (to.y - from.y) * t;
    out[i * 3 + 2] = from.z + (to.z - from.z) * t;
  }
}

/** A soft round dot, drawn rather than shipped.
 *
 *  PointsMaterial renders plain points as squares. On a dark field at
 *  this size a square reads as a dot anyway, but a radial falloff looks
 *  deliberate instead of like a missing texture. Generated for the same
 *  reason the basemap was: the project self-hosts and carries no binary
 *  assets.
 */
export function createDotTexture(): THREE.CanvasTexture {
  const size = 64;
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('tether dots: no 2d context');
  const g = ctx.createRadialGradient(size / 2, size / 2, 0, size / 2, size / 2, size / 2);
  g.addColorStop(0, 'rgba(255,255,255,1)');
  g.addColorStop(0.5, 'rgba(255,255,255,0.9)');
  g.addColorStop(1, 'rgba(255,255,255,0)');
  ctx.fillStyle = g;
  ctx.beginPath();
  ctx.arc(size / 2, size / 2, size / 2, 0, Math.PI * 2);
  ctx.fill();
  const tex = new THREE.CanvasTexture(canvas);
  tex.colorSpace = THREE.SRGBColorSpace;
  return tex;
}

export function tetherAnchors(
  hero: { x: number; z: number; baseY: number; scale: number },
  mob: { x: number; z: number; groundY: number; base: number; lift: number },
): [Anchor, Anchor] {
  return [
    { x: hero.x, y: hero.baseY + HERO_HALF_HEIGHT * hero.scale, z: hero.z },
    { x: mob.x, y: mob.groundY + mob.base / 2 + mob.lift, z: mob.z },
  ];
}

interface Anim {
  key: string;
  kind: AnimKind;
  t0: number;
  mesh: THREE.Mesh;
  base: number;
  /** Stack lift, so animation restores the piece to its own height
   *  rather than dropping it to the ground. */
  lift: number;
  spark: THREE.Mesh;
  sparkMat: THREE.MeshBasicMaterial;
}

export function BattlemapPage() {
  const mountRef = useRef<HTMLDivElement | null>(null);
  /** Survives scene rebuilds. A WebGL context costs ~120ms to build. */
  const rendererRef = useRef<THREE.WebGLRenderer | null>(null);
  /**
   * The camera and its controls also survive a rebuild, and that is the
   * difference between a replay you can watch and one you cannot.
   *
   * A replay advances one event at a time, and every event rebuilds the
   * scene. While the camera was built inside that scene, every event threw
   * the operator's viewpoint away and snapped back to the default three-
   * quarter view — so following one ticket meant losing your place several
   * times a second, and moving around the board while the animation ran was
   * impossible.
   */
  const cameraRef = useRef<THREE.PerspectiveCamera | null>(null);
  const controlsRef = useRef<OrbitControls | null>(null);
  const [showGraveyard, setShowGraveyard] = useState(true);
  // The legend is sixteen rows and sits over the board's lower left. It is
  // worth having open while learning the board and worth getting out of the
  // way after, so it toggles like the other overlays rather than always
  // being one or the other.
  const [showLegend, setShowLegend] = useState(false);
  /** Shadows. On by default; the toggle is here because the cost depends
   *  on the operator's GPU and I cannot measure that from here. */
  const [shadows, setShadows] = useState(true);
  const [spin, setSpin] = useState(false);
  const [stats, setStats] = useState({ fps: 0, drawCalls: 0, tris: 0 });
  const [combat, setCombat] = useState({ played: 0, kills: 0 });
  const [staleCount, setStaleCount] = useState(0);
  // Replay. Off by default: the board's job is to show NOW, and a replay
  // is something you ask for. `playheadMs` is null in live mode so the two
  // modes cannot both claim to be driving the board.
  const [replayMode, setReplayMode] = useState(false);
  const [playheadMs, setPlayheadMs] = useState<number | null>(null);
  const [playing, setPlaying] = useState(false);
  const [speed, setSpeed] = useState<number>(DEFAULT_SPEED);
  /** Live comment pings. DOM, not scene objects — see pieceScreenRef. */
  const [pings, setPings] = useState<Array<{
    id: number; key: string; kind: PingKind; x: number; y: number;
    /** Where the ray starts: the figure of whoever the mark is aimed at —
     *  the actor for an ordinary event, the ASSIGNEE for a regression. */
    from: { x: number; y: number } | null;
    /** A transition that moved the ticket backwards. */
    backwards: boolean;
    /** The first move of work that was never committed — a new challenge
     *  appearing. Shares the red-and-exclamation treatment with a
     *  regression; see isAlarm in playback.ts. */
    arrived: boolean;
  }>>([]);
  const pingSeqRef = useRef(0);

  // The playback clock's own state, in refs.
  //
  // NOT in the effect's dependency list, and that is the whole point. The
  // first version depended on `instants`, which is derived from the
  // timeline query — so a refetch (every 20s while mounted) gave it a new
  // array identity, tore the effect down and re-seeded the playhead from a
  // stale closure. Measured in a browser: playback ran at 9 events/second,
  // then at 0.5, instead of the selected 4.
  //
  // A render loop is a mount-lifetime concern. It reads the current data
  // through refs and is started and stopped by `playing` alone.
  const playRef = useRef({ index: 0, carry: 0, last: 0, lastFired: 0 });
  const speedRef = useRef<number>(DEFAULT_SPEED);
  const firePingsRef = useRef<(a: number, b: number) => void>(() => {});
  const instantsRef = useRef<number[]>([]);
  const timelineRef = useRef<BattlemapTimeline | null>(null);
  const [selectedHero, setSelectedHero] = useState<string | null>(null);
  const [selectedMob, setSelectedMob] = useState<string | null>(null);
  const combatRef = useRef({ played: 0, kills: 0 });
  const [searchParams, setSearchParams] = useSearchParams();
  // Live-event plumbing. The scene is rebuilt whenever the layout
  // changes; the animation state lives in refs so it survives those
  // rebuilds and so the render loop can read it without re-rendering
  // React on every frame.
  const mobMeshesRef = useRef(new Map<string, { mesh: THREE.Mesh; base: number; lift: number }>());
  const animQueueRef = useRef<Array<{ key: string; kind: AnimKind }>>([]);
  const animsRef = useRef<Anim[]>([]);
  const sceneRef = useRef<THREE.Scene | null>(null);
  // Tickets we've killed on screen but the data hasn't caught up on
  // yet. Without this, a completed kill animation would pop back to a
  // live mob on the next 15s rebuild.
  const killedLocallyRef = useRef(new Set<string>());
  const seenEventsRef = useRef(new Set<string>());
  // Survives scene rebuilds, so a 15s data refresh doesn't drop your
  // selection. The render loop reads the ref, not React state, so
  // clicking a hero doesn't re-render the page.
  const selectedHeroRef = useRef<string | null>(null);
  /**
   * The ONE hero whose name is showing, and until when.
   *
   * Operator: "if the same person fires an event again, the name doesn't
   * disappear then reappear - the name stays solid until a new person fires
   * an event."
   *
   * A single id rather than a map of deadlines, and that is the whole
   * mechanism:
   *
   *   same person fires again  -> the id is unchanged and only the deadline
   *                               moves, so the name never turns off and on
   *   a different person fires -> the id is replaced, so the old name clears
   *                               and the new one appears
   *
   * A map keyed by person would show SEVERAL names at once and each would
   * blink out on its own deadline, which is not what "one flash of the name"
   * means.
   *
   * Still a ref, so it survives the scene rebuild that every event causes.
   */
  const pingNamedRef = useRef<{ id: string | null; until: number }>({ id: null, until: 0 });
  const selectedMobRef = useRef<string | null>(null);
  const selectionRingRef = useRef<THREE.Mesh | null>(null);
  // The tether rebuild lives inside the scene effect's closure (it
  // needs the scene and the meshes), so it's published here for the
  // outside world to call — the footer's 'clear' button, mainly.
  const refreshTethersRef = useRef<(() => void) | null>(null);
  /** key -> screen position, published by the scene effect. DOM pings need
   *  it and cannot reach into the effect's closure. */
  const pieceScreenRef = useRef<((key: string) => { x: number; y: number } | null) | null>(null);
  /** user id -> screen position, for the attribution rays. */
  const heroScreenRef = useRef<((id: string) => { x: number; y: number } | null) | null>(null);
  // Staleness threshold, tunable from the URL while evaluating what
  // reads well: ?stale=2
  const staleDays = (() => {
    const v = Number(searchParams.get('stale'));
    return Number.isFinite(v) && v > 0 ? v : DEFAULT_STALE_DAYS;
  })();
  // Same idea for the dot march: ?dot=5000. Floored at 200ms so a typo
  // cannot produce a strobe.
  const dotCycleMs = (() => {
    const v = Number(searchParams.get('dot'));
    return Number.isFinite(v) && v >= 200 ? v : DEFAULT_DOT_CYCLE_MS;
  })();
  // ?dotpx=3
  const dotPx = (() => {
    const v = Number(searchParams.get('dotpx'));
    return Number.isFinite(v) && v > 0 && v <= 40 ? v : DEFAULT_DOT_PX;
  })();

  const teams = useQuery({
    queryKey: ['teams'],
    queryFn: () => api<{ teams: Team[] }>('/api/teams'),
    enabled: true,
  });
  const teamId = searchParams.get('team_id') || teams.data?.teams?.[0]?.id || '';
  const setTeamId = (id: string) => {
    const next = new URLSearchParams(searchParams);
    if (id) next.set('team_id', id); else next.delete('team_id');
    setSearchParams(next, { replace: true });
  };

  const live = useQuery({
    queryKey: ['battlemap', teamId],
    queryFn: () => api<BattlemapPayload>(`/api/admin/battlemap?team_id=${encodeURIComponent(teamId)}`),
    enabled: !!teamId,
    refetchInterval: 15_000,
  });

  // Reuse the public board's live feed rather than adding an endpoint:
  // it already returns the ticket key in the payload and polls fast
  // enough to feel live. We only read the ticket key, the event type
  // and the id out of it.
  // The replay window: static structure plus the ordered event stream, in
  // one call. Only fetched when replay is actually on.
  const timeline = useQuery({
    queryKey: ['battlemap-timeline', teamId],
    queryFn: () => api<BattlemapTimeline>(
      `/api/admin/battlemap/timeline?team_id=${encodeURIComponent(teamId)}`,
    ),
    enabled: replayMode && !!teamId,
    // The window runs to NOW, so entering replay after a while should
    // pick up what has happened since. Cheap: one call for the whole
    // campaign, and it is only fetched while replay is open.
    staleTime: 20_000,
  });

  const replayWindow = useMemo(() => {
    if (!timeline.data) return null;
    return {
      from: alignToLocalHour(Date.parse(timeline.data.window.from)),
      to: Date.parse(timeline.data.window.to),
    };
  }, [timeline.data]);

  const buckets = useMemo(
    () => (timeline.data && replayWindow
      ? hourBuckets(timeline.data.events, replayWindow.from, replayWindow.to)
      : []),
    [timeline.data, replayWindow],
  );
  const gaps = useMemo(() => quietGaps(buckets, 3), [buckets]);
  const instants = useMemo(
    () => (timeline.data ? stepInstants(timeline.data.events) : []),
    [timeline.data],
  );
  instantsRef.current = instants;
  timelineRef.current = timeline.data ?? null;
  speedRef.current = speed;

  // Comments and closing instants, computed once per timeline rather than
  // per frame: 307 comments in a live window, and re-walking the stream
  // sixty times a second would be the expensive part of playback.
  // Work that ARRIVED during this window: never committed, and moved at
  // some point. Exactly the set the board draws as orange reinforcements,
  // so the exclamation marks the pieces that appeared unplanned and nothing
  // else.
  //
  // Computed from the whole timeline rather than from the playhead,
  // deliberately: whether a ticket was ever an arrival does not depend on
  // when you are looking at it. An arrival that later closed is still an
  // arrival, and the mark should still fire at the moment it appeared.
  const arrivalKeys = useMemo(() => {
    if (!timeline.data) return [] as string[];
    const committed = new Set(timeline.data.committed);
    const keys = new Set<string>();
    for (const e of timeline.data.events) {
      if (e.kind === 'transition' && !committed.has(e.key)) keys.add(e.key);
    }
    return [...keys];
  }, [timeline.data]);

  const pingEvents = useMemo(
    () => (timeline.data ? pingStream(timeline.data.events, arrivalKeys) : []),
    [timeline.data, arrivalKeys],
  );
  const closedAt = useMemo(
    () => (timeline.data ? closingInstants(timeline.data.events, timeline.data.terminal_status_names) : new Map()),
    [timeline.data],
  );

  /** Fire pings for the comments the playhead just crossed, then retire
   *  them. A ping is ephemeral: it exists to say "something happened here",
   *  and it is gone in under a second. */
  const firePings = (fromMs: number, toMs: number) => {
    const hits = pingsBetween(pingEvents, fromMs, toMs, closedAt);
    if (hits.length === 0) return;
    // The projectors below are what put a mark ON the screen. If one is
    // missing, every hit resolves to "no position" and NOTHING is drawn —
    // silently, with no error and nothing to see. That has happened twice to
    // the same two lines, the second time after a comment warning about the
    // first. A comment is not a guard; this is.
    if (!pieceScreenRef.current) {
      console.warn(
        '[battlemap] pieceScreenRef is not assigned, so event marks cannot be positioned. ' +
        'The screen projectors in the scene effect have been deleted again.',
      );
      return;
    }

    const born = hits.flatMap((h) => {
      const pos = pieceScreenRef.current?.(h.key);
      // No position means the piece is not on the board at this instant —
      // already closed, or not yet arrived. A ping with nowhere to land is
      // not worth drawing.
      if (!pos) return [];
      // The ray's origin. A hero may simply not be on the board — they
      // could be unassigned and uninvolved — in which case the event is
      // still marked, just not attributed.
      //
      // A BACKWARDS move aims at the ASSIGNEE rather than at the actor, and
      // that is the point of marking it separately: the person who sends
      // work back is usually not the person accountable for it, and a red
      // line to whoever happened to move it would blame the wrong desk.
      // Falls back to the actor when nobody is assigned — some origin beats
      // none — and to nothing when neither has a figure on the board.
      const aimedAt = isAlarm(h) ? (h.assignee ?? h.actor) : h.actor;
      const from = aimedAt ? heroScreenRef.current?.(aimedAt) ?? null : null;
      // NAME THEM WHILE THEIR LINE IS UP, FOR ONE FLASH.
      //
      // Operator: "I just need one flash of the name, for half a second."
      //
      // A deadline in a ref, so it SURVIVES the scene rebuild that every event
      // causes — the element is recreated four times a second during a replay
      // and the deadline is what makes all four of those the same continuous
      // flash rather than four separate ones.
      // Same person again: the id does not change, so the name stays on and
      // only the deadline moves. Different person: the id is replaced and the
      // old name goes. Either way there is exactly one name at a time.
      if (from && aimedAt) {
        pingNamedRef.current = { id: aimedAt, until: performance.now() + 500 };
      }
      return [{
        id: (pingSeqRef.current += 1),
        key: h.key,
        kind: h.kind,
        x: pos.x,
        y: pos.y,
        from,
        backwards: h.backwards === true,
        arrived: h.arrived === true,
      }];
    });
    if (born.length === 0) return;
    setPings((prev) => [...prev, ...born]);
    const ids = new Set(born.map((b) => b.id));
    // Long enough for the ray to draw AND the indicator to follow it.
    setTimeout(() => setPings((prev) => prev.filter((p) => !ids.has(p.id))), 1300);
  };
  firePingsRef.current = firePings;

  // The playback clock. Advances the playhead by EVENTS, so every step is
  // the same amount of story however busy the hour was.
  //
  // Depends on `playing` ALONE - see the playRef note above for why.
  useEffect(() => {
    if (!playing || !replayMode) return;
    let raf = 0;
    playRef.current.last = performance.now();
    playRef.current.lastFired = playheadMs ?? 0;

    const tick = () => {
      const list = instantsRef.current;
      if (list.length === 0) {
        setPlaying(false);
        return;
      }
      const now = performance.now();
      const dt = now - playRef.current.last;
      playRef.current.last = now;

      const f = advancePlayback(list.length, playRef.current.index, speedRef.current, dt, playRef.current.carry);
      playRef.current.index = f.index;
      playRef.current.carry = f.carry;

      const ms = instantAtIndex(list, f.index, playRef.current.lastFired);
      if (ms !== playRef.current.lastFired) {
        // The span just crossed is (previous, current], and it MUST be read
        // before lastFired is advanced. Advancing first asks the ping to
        // cover (current, current] - an EMPTY window - and nothing is ever
        // drawn. That failure is silent: playback still runs and the board
        // still moves, so it reads as "the animations are gone" rather than
        // as a broken ping.
        const crossedFrom = playRef.current.lastFired;
        playRef.current.lastFired = ms;
        // Set only when the EVENT changes. Setting it every frame would
        // rebuild the scene sixty times a second, and the rebuild
        // measurement above says that is not free.
        setPlayheadMs(ms);
        try {
          firePingsRef.current(crossedFrom, ms);
        } catch {
          // A failed flourish must never stop the replay. Swallowed
          // deliberately: there is nothing the operator can do about it, and
          // the alternative is a dead board with a dead animation and no
          // explanation for either.
        }
      }
      if (f.done) {
        setPlaying(false);
        return;
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [playing, replayMode]);

  // Land the playhead at the END of the window when replay first opens, so
  // the board starts as it is now and is wound BACKWARDS. Opening at the
  // beginning would show an empty field, which reads as broken.
  useEffect(() => {
    if (replayMode && replayWindow && playheadMs === null) setPlayheadMs(replayWindow.to);
  }, [replayMode, replayWindow, playheadMs]);

  const events = useQuery({
    queryKey: ['battlemap-events', teamId],
    queryFn: () => api<{ events: Array<{ id: string; type: string; payload?: Record<string, unknown> }> }>(
      `/api/public-board/live-feed?team_id=${encodeURIComponent(teamId)}`,
    ),
    enabled: !!teamId,
    refetchInterval: 2_500,
  });

  useEffect(() => {
    if (!events.data) return;
    const seen = seenEventsRef.current;
    const queue = animQueueRef.current;
    // Newest first from the API. Queue oldest-first so simultaneous
    // events animate in the order they happened.
    const fresh = events.data.events.filter((e) => !seen.has(e.id)).reverse();
    for (const e of fresh) {
      seen.add(e.id);
      const key = typeof e.payload?.issue_key === 'string' ? e.payload.issue_key : null;
      if (!key) continue;
      queue.push({ key, kind: kindFor(e.type, e.payload) });
    }
    // Cap the seen-set so a long session doesn't grow it forever.
    if (seen.size > 500) seenEventsRef.current = new Set(Array.from(seen).slice(-200));
    // Don't let a backlog replay for minutes if the tab was hidden.
    if (queue.length > 40) animQueueRef.current = queue.slice(-40);
  }, [events.data, teamId]);


  // The owners of whichever board is being shown. Replay reads the
  // timeline's list; live reads the snapshot's. They agree today, but
  // reading the ACTIVE one means they cannot silently disagree later, and
  // it was the live list alone while replaying.
  const activeOwners = useMemo(
    () => (replayMode ? timeline.data?.owners : live.data?.owners) ?? [],
    [replayMode, timeline.data, live.data],
  );

  // Who the heroes are, and what colour each one is.
  //
  // NOT `o.color` alone. Every member of this team has an EMPTY chip_color,
  // so that map was empty and every hero fell through to one shared fallback:
  // twenty-six people in the same colour, while the legend claimed colour
  // tells you which person. A colour is now derived per handle when the
  // database has none. See features/battlemap/hero-colour.ts.
  const heroColorById = useMemo(() => {
    const m = new Map<string, string>();
    for (const o of activeOwners) m.set(o.id, heroColourFor(o.handle, o.color));
    return m;
  }, [activeOwners]);
  // Which owners are non-human. Drives the figure's colour and the label
  // prefix so automation is identifiable at a glance rather than looking
  // like another teammate.
  const botIds = useMemo(() => {
    const set = new Set<string>();
    for (const o of activeOwners) {
      if (o.is_bot) set.add(o.id);
    }
    return set;
  }, [activeOwners]);
  // Ownership vs assistance, straight from the payload.
  const assigneeOf = useMemo(
    () => (live.data?.assignee_of) ?? {},
    [live.data],
  );
  const actorsOf = useMemo(
    () => (live.data?.actors_of) ?? {},
    [live.data],
  );

  const heroNameById = useMemo(() => {
    const m = new Map<string, string>();
    for (const o of activeOwners) m.set(o.id, o.name ?? `@${o.handle}`);
    return m;
  }, [activeOwners]);
  // The board as of the playhead. Computed ONCE and shared, because the
  // click-a-piece panel needs the same fold's status and re-running it
  // would let the two disagree.
  const replayLayout = useMemo(
    () => (replayMode && timeline.data && playheadMs !== null
      ? layoutInputAt(timeline.data, playheadMs)
      : null),
    [replayMode, timeline.data, playheadMs],
  );

  /** Click-a-piece facts while replaying: the static summary and type from
   *  the timeline, the status as of the playhead rather than as of now. */
  const replayTicketInfo = useMemo(() => {
    if (!replayLayout || !timeline.data) return null;
    const out: Record<string, { status: string; summary: string | null; type: string | null }> = {};
    for (const [k, v] of Object.entries(timeline.data.ticket_info)) {
      out[k] = { status: replayLayout.statusOf[k] ?? 'unknown', summary: v.summary, type: v.type };
    }
    return out;
  }, [replayLayout, timeline.data]);

  const data: LayoutInput = useMemo(
    // No campaign yet: a real empty input rather than a fixture's shape.
    // The frame does not resize with the data, so this draws an empty
    // board rather than nothing at all.
    () =>
      replayLayout
        ? (replayLayout as LayoutInput)
        : live.data
        ? toLayoutInput(live.data)
        : {
            committed: [], cleared: [], goals: [], sideQuests: [], reinforcements: [],
            bosses: [], ownerOf: {}, assigneeOf: {}, actorsOf: {}, statusOf: {},
            minionOf: {}, lastTouchOf: {},
            startsAt: new Date().toISOString(),
            endsAt: new Date(Date.now() + 1).toISOString(),
          },
    [live.data, replayLayout],
  );
  const field = useMemo(() => layoutBattlefield(data), [data]);
  // Committed pieces and mid-sprint arrivals are the same kind of thing
  // once placed, and every lookup, tether and hit test must see both or
  // the arrivals would be drawn but unclickable.
  // Closed work leaves the board rather than settling on it as rubble.
  // Filtered HERE, once, so the scene, the hit test and the tethers all
  // agree — the previous arrangement skipped the mesh at draw time,
  // which left invisible pieces that still took clicks.
  const allPieces = useMemo(
    () => [...field.mobs, ...field.reinforcements].filter((m) => !m.cleared),
    [field],
  );

  // Selection changed (by click or by the footer's clear button) →
  // redraw the tethers. The scene owns the geometry, so it has to be
  // asked rather than rebuilt from React.
  useEffect(() => {
    refreshTethersRef.current?.();
  }, [selectedHero, selectedMob]);

  useEffect(() => {
    placeSelectionRing(selectionRingRef.current, allPieces, selectedMob);
  }, [selectedMob, field]);

  // What to say about the clicked piece. Recomputed from the payload
  // rather than stashed at click time, so it stays true as data
  // refreshes instead of freezing at the moment of the click.
  const selectedFacts = useMemo(() => {
    if (!selectedMob) return null;
    const mob = allPieces.find((m) => m.key === selectedMob) ?? null;
    const info = (replayMode ? replayTicketInfo : live.data?.ticket_info)?.[selectedMob] ?? null;
    // The piece's owner is the most recent actor; the assignee is a
    // separate fact and the one that means accountability.
    const actorId = mob?.ownerId ?? null;
    const assigneeId = assigneeOf[selectedMob] ?? null;
    return ticketFacts({
      key: selectedMob,
      mob,
      info,
      assigneeId,
      assigneeName: assigneeId ? heroNameById.get(assigneeId) ?? assigneeId : null,
      actorId,
      actorName: actorId ? heroNameById.get(actorId) ?? actorId : null,
      actorIsBot: actorId ? botIds.has(actorId) : false,
      goalName: mob?.goalId
        ? field.goals.find((g) => g.id === mob.goalId)?.name ?? null
        : null,
      minionBossName: mob?.minionBossId
        ? field.bosses.find((b) => b.id === mob.minionBossId)?.name ?? null
        : null,
      now: Date.now(),
      staleDays,
    });
  }, [selectedMob, field, live.data, replayMode, replayTicketInfo, assigneeOf, heroNameById, botIds, staleDays]);

  const loading = teams.isLoading || live.isLoading;
  const failed = teams.isError || live.isError;
  const noAdventure = !!live.data && live.data.adventure === null;

  // Rebuild the scene whenever the layout or a toggle changes.
  // Rebuilding wholesale is fine at this scale and keeps the code
  // simple; Phase 1 will move to incremental scene updates driven by
  // the event feed.
  useEffect(() => {
    const mount = mountRef.current;
    if (!mount) return;

    const scene = new THREE.Scene();
    sceneRef.current = scene;
    mobMeshesRef.current = new Map();
    // Any in-flight animation's meshes belong to the previous scene.
    animsRef.current = [];
    // The fog colour IS the sky's horizon colour. The board fades into
    // the sky at its far edge, so the two have to meet at the same tone
    // or there is a visible line where the ground stops.
    scene.fog = new THREE.Fog('#2e2b26', 150, 320);

    let camera = cameraRef.current;
    if (!camera) {
      camera = new THREE.PerspectiveCamera(50, mount.clientWidth / mount.clientHeight, 0.1, 1000);
      camera.position.set(0, 70, 105);
      cameraRef.current = camera;
    }
    camera.aspect = mount.clientWidth / mount.clientHeight;
    camera.updateProjectionMatrix();

    // alpha: true so the SKY can be a CSS gradient behind the canvas
    // rather than anything WebGL draws.
    //
    // The first attempt put a gradient texture on scene.background. That
    // is a full-screen textured quad — the same cost that got the table
    // dropped, and it measured twelve of the frame on the software
    // renderer used to check it. Claiming otherwise was simply wrong:
    // "no draw calls" is not "nothing to rasterise".
    //
    // Letting the browser composite it costs the GPU nothing at all.
    // The renderer OUTLIVES a rebuild, and that is not a tidy-up.
    //
    // Measured: the whole scene build is 12.9ms warm, but the effect
    // containing it took 134ms. The difference was almost entirely
    // `renderer.dispose()` on teardown followed by a new WebGLRenderer —
    // destroying and recreating the WebGL context, about 120ms a time.
    //
    // Reusing it removes that, and keeps three.js's compiled-shader cache
    // alive, so a rebuild stops recompiling every material's program. A
    // replay advances one event per rebuild, so at 4 events a second that
    // was most of a second spent rebuilding.
    let renderer = rendererRef.current;
    if (!renderer) {
      renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
      rendererRef.current = renderer;
    }
    renderer.setClearAlpha(0);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    if (renderer.domElement.parentElement !== mount) mount.appendChild(renderer.domElement);
    renderer.setSize(mount.clientWidth, mount.clientHeight);
    // Shadows are what stop a board of primitives looking like a scatter
    // of floating bricks: nothing here touches the ground without them.
    // The Pi 4 budget this scene was first built under is gone — the
    // battlemap is a desktop view — so a shadow pass is affordable. The
    // toggle exists because I cannot measure the operator's GPU.
    renderer.shadowMap.enabled = shadows;
    renderer.shadowMap.type = THREE.PCFSoftShadowMap;

    let controls = controlsRef.current;
    if (!controls) {
      controls = new OrbitControls(camera, renderer.domElement);
      controls.target.set(0, 0, -30);
      controls.maxPolarAngle = Math.PI / 2.05;
      controls.minDistance = 25;
      controls.maxDistance = 320;
      controls.enableDamping = true;
      controls.dampingFactor = 0.08;
      controlsRef.current = controls;
    }
    // These are the only settings that come from React, so they are the only
    // ones re-applied per rebuild.
    controls.autoRotate = spin;
    controls.autoRotateSpeed = 0.4;

    // --- lighting: a key, a fill, and a sky --------------------------
    // Was one flat ambient plus one warm key, which left every primitive
    // evenly lit and therefore shapeless. Three lights now, and each does
    // a job a shape needs:
    //
    //   key   the warm directional, from the enemy's side
    //   fill  a cool directional opposite it, so every face has a lit side
    //         and a shadow side instead of one flat tone
    //   sky   a hemisphere replacing the ambient: cool from above, warm
    //         off the ground, which is also the bounce light a real
    //         tabletop would give
    //
    // Deliberately kept near-neutral in aggregate. The palette ENCODES
    // MEANING — gold is a goal, orange is unplanned, crimson is a minion —
    // so a lighting change can break the legend even while looking nicer.
    // Measured afterwards rather than eyeballed; see the verification note.
    scene.add(new THREE.HemisphereLight(0xdde3ea, 0x5a5a60, 1.15));
    const dir = new THREE.DirectionalLight(0xfff6e8, 1.25);
    dir.position.set(40, 90, 40);
    if (shadows) {
      dir.castShadow = true;
      // Sized to the board (x ±58, z -80..32) plus the piece height, so
      // the shadow map spends its resolution on the battlefield rather
      // than on empty dark.
      const S = 95;
      dir.shadow.camera.left = -S;
      dir.shadow.camera.right = S;
      dir.shadow.camera.top = S;
      dir.shadow.camera.bottom = -S;
      dir.shadow.camera.near = 1;
      dir.shadow.camera.far = 320;
      dir.target.position.set(0, 0, -25);
      scene.add(dir.target);
      dir.shadow.mapSize.set(2048, 2048);
      // Small negative bias: without it the flat ground plane develops
      // acne where a piece's own shadow is cast onto itself.
      dir.shadow.bias = -0.0008;
      dir.shadow.normalBias = 0.02;
    }
    scene.add(dir);
    // Cool fill, opposite the key. Low enough to read as bounce rather
    // than as a second sun.
    const fill = new THREE.DirectionalLight(0xcdd4e0, 0.28);
    fill.position.set(-60, 45, -60);
    scene.add(fill);

    // Bots get a fixed violet rather than their chip colour, so the
    // distinction doesn't depend on a colour someone picked by hand.
    const heroColorFor = (id: string) => (botIds.has(id) ? '#a06bff' : heroColorById.get(id) ?? HERO_FALLBACK);
    const heroNameFor = (id: string) => {
      const base = heroNameById.get(id) ?? id;
      return botIds.has(id) ? `🤖 ${base}` : base;
    };
    const onSelectionChange = setSelectedHero;
    const onMobSelectionChange = setSelectedMob;
    setStaleCount(0);
    // Nothing is tracked for disposal any more: see the sharedAssets note
    // above for why the scene's materials outlive a rebuild.

    // --- ENVIRONMENT -------------------------------------------------
    // Metal with nothing to reflect renders black. That is not a
    // stylistic choice, it is what a metalness of 0.9 DOES: metal has no
    // diffuse term at all, so it shows only what it mirrors. A metallic
    // look therefore needs an environment before it needs anything else.
    //
    // Procedural, like everything else here: an equirectangular canvas
    // drawn as a dusk sky — dark ground, a warm band at the horizon, a
    // soft key at one side — and run through PMREMGenerator so roughness
    // has something mip-mapped to sample.
    //
    // Deliberately BRIGHTER than the visible scene. Reflections are the
    // only light a metal surface gets, so an environment that matched the
    // battlefield's own darkness would make every metallic thing in it
    // read as a black hole.
    {
      const c = document.createElement('canvas');
      c.width = 256;
      c.height = 128;
      const g = c.getContext('2d');
      if (g) {
        const sky = g.createLinearGradient(0, 0, 0, 128);
        sky.addColorStop(0.00, '#2b3448');
        sky.addColorStop(0.40, '#4b4334');
        sky.addColorStop(0.52, '#6d5730'); // the horizon, at the equator
        sky.addColorStop(0.70, '#18150f');
        sky.addColorStop(1.00, '#0b0a09');
        g.fillStyle = sky;
        g.fillRect(0, 0, 256, 128);
        // A soft warm key off to one side. Metal needs a highlight to
        // glint on, and a flat gradient gives it a mirror with no source.
        const glow = g.createRadialGradient(168, 32, 0, 168, 32, 72);
        glow.addColorStop(0, 'rgba(255,238,205,0.95)');
        glow.addColorStop(1, 'rgba(255,238,205,0)');
        g.fillStyle = glow;
        g.fillRect(0, 0, 256, 128);

        const src = new THREE.CanvasTexture(c);
        src.mapping = THREE.EquirectangularReflectionMapping;
        src.colorSpace = THREE.SRGBColorSpace;
        const pmrem = new THREE.PMREMGenerator(renderer);
        const rt = pmrem.fromEquirectangular(src);
        scene.environment = rt.texture;
        // Well under 1: this is a night battlefield, not a showroom, and
        // the pieces still have to read by the lights that were already
        // there.
        scene.environmentIntensity = 0.85;
        shared('pmremRt', () => rt);
        src.dispose();
        pmrem.dispose();
      }
    }

    // --- THE BOARD ---------------------------------------------------
    // The battlefield used to be a plane floating in void: it simply
    // ended, with nothing under it. Now it has an EDGE, so it reads as an
    // object with thickness rather than as a decal.
    //
    // There was a TABLE under it too — a wide surface extending past the
    // board on every side, faded out by the fog, to give the eye a
    // horizon. Dropped on the operator's call: it is two draw calls and
    // fourteen triangles, but a full-screen quad of shaded pixels, and it
    // measured a third of the frame on the software renderer used to
    // verify this. A real GPU would not notice; the operator's machine
    // was not something I could measure, so the version that costs
    // nothing everywhere won.
    //
    // The slab stays because it is small on screen — its top is hidden
    // under the ground planes and only the sides are ever seen.
    {
      const slabW = PLANE_X_HALF * 2 + 8;
      const slabD = (PLANE_Z_NEAR - PLANE_Z_FAR) + 8;
      const slabGeo = shared('slabGeo', () => new THREE.BoxGeometry(slabW, 0.7, slabD));
      // #5a6472, up from #2a2f36. The slab's side faces point at the camera
      // and away from the key light, so they render at about a fifth of
      // their material: the original measured a 5 luminance step against
      // the background, which is not an edge. This is the one number to
      // turn if it still does not read.
      const slabMat = shared('slabMat', () => new THREE.MeshLambertMaterial({ color: '#5a6472' }));
      const slab = new THREE.Mesh(slabGeo, slabMat);
      // Top at -0.05, just BELOW the ground planes at -0.02 and -0.01.
      // Flush would put two surfaces on the same y and z-fight along the
      // whole board; 0.03 of clearance is invisible and safe.
      slab.position.set(0, -0.40, (PLANE_Z_FAR + PLANE_Z_NEAR) / 2);
      scene.add(slab);
    }

    // --- GROUND -----------------------------------------------------
    // One plane. No time split, no boundary line: the ground is the
    // battlefield, not a clock. Depth is distance to the enemy — boss
    // keep far, side quests nearest. See the header comment in
    // layout.ts for why the time axis was removed.
    //
    // Width and depth are FIXED, not derived from ticket counts: the
    // frame must not resize when the commitment changes, or every
    // piece would appear to shift.
    const fieldWidthWorld = PLANE_X_HALF * 2;
    const planeDepth = PLANE_Z_NEAR - PLANE_Z_FAR;
    {
      // One plane, painted. Was a flat #1b2430 quad with a separate darker
      // strip for the graveyard; the texture now carries every band, the
      // grid and the edge falloff, so the strip mesh is gone and the
      // ground is one draw call instead of two.
      const g = new THREE.PlaneGeometry(fieldWidthWorld, planeDepth);
      const tex = shared('groundTex', () => createBasemapTexture());
      // Back to Lambert deliberately. The ground covers most of the
      // screen, it is matte (roughness 0.95) and it is not metal, so PBR
      // buys it nothing and charges full price for every one of those
      // pixels. Physical materials are for the things that are actually
      // metallic.
      const m = shared('groundMat', () => new THREE.MeshLambertMaterial({ map: tex }));
      const mesh = new THREE.Mesh(g, m);
      mesh.rotation.x = -Math.PI / 2;
      mesh.position.set(0, -0.02, PLANE_Z_FAR + planeDepth / 2);
      mesh.receiveShadow = shadows;
      scene.add(mesh);
    }

    // --- MOBS -------------------------------------------------------
    // Grey boxes on the field. Cleared ones stay as dark rubble so
    // you can see the field emptying out.
    // A beast per appendage count, so the piece's SHAPE is its status.
    // Six geometries, built once and shared by every piece that has that
    // many horns. See features/battlemap/beast.ts for why the horns are
    // lost from the tallest down.
    const beastGeoFor = (n: number) =>
      shared(`mobGeo-${n}`, () => buildBeastGeometry(n, MOB_SIZE));

    // How tall that beast actually is.
    //
    // MOB_SIZE is a WIDTH constant, and it was being used as a height: a
    // piece was placed at `groundY + MOB_SIZE / 2`, which is correct only
    // for a piece exactly MOB_SIZE tall. The beast is not - it runs from
    // 0.81 units with no horns to 2.07 with five.
    //
    // The result was that a nearly-dead beast FLOATED a tenth of a unit
    // above the field and a healthy one was buried almost half a unit
    // under it. Exactly backwards, and reported as "the mobs still seem to
    // be standing below the surface".
    //
    // The bounding box is already computed inside the factory, for the
    // centring translate, and that translate moves both ends equally, so
    // the height here is correct at no extra cost.
    const beastHeightFor = (n: number): number => {
      const g = beastGeoFor(n);
      const bb = g.boundingBox;
      return bb ? bb.max.y - bb.min.y : MOB_SIZE;
    };
    // A limbless body, for the goal-orphan marker: a goal listing a key that
    // is not committed is a configuration mistake, and a corpse is the right
    // shape for one.
    const mobGeo = beastGeoFor(0);
    const tombGeo = shared('tombGeo', () => new THREE.BoxGeometry(TOMB_W, TOMB_H, TOMB_D));
    // --- SHIELD -----------------------------------------------------
    // Blocked work carries a shield. SHAPE rather than colour: the body
    // colour is already saying which KIND of work a piece is, and a
    // blocked ticket can be any kind — committed, a goal, unplanned.
    // Depth is the same channel as open-or-closed; sinking is how close
    // to done. None of them could carry "stopped" without lying about
    // something else.
    //
    // THE SWORD AND THE SHIELD ARE GONE, and so is every geometry and
    // material that drew them.
    //
    // They carried one bit each, as a separate object standing in front of
    // the piece, and they could only be read with the legend open. Status is
    // now the SHAPE OF THE PIECE: see features/battlemap/beast.ts. One
    // object instead of two, and legible without a key.

    // Body colour carries GOAL MEMBERSHIP, not minion-ness.
    //
    // It used to carry minion-ness, which meant boss-red won whenever
    // a ticket was both. On live data 13 of the 15 goal tickets are
    // also minions, so nearly every encampment rendered crimson and
    // goal membership was invisible. Goal membership is the more
    // useful thing to see — it's the named part of the commitment —
    // and minion-ness now gets its own channel (the ground ring
    // below) instead of competing for the same one.
    // Painted miniatures: a slight sheen, not metal. metalness stays low or
    // the palette loses its saturation — metal has no diffuse term.
    // Committed work is now a VERY PALE GOLD rather than a grey.
    //
    // Operator's call, and it makes the field read as one family: a pale
    // piece is work you promised, a full-gold piece is work that counts
    // toward a goal, and the two are the same metal at different
    // intensities rather than two unrelated colours.
    //
    // Worth knowing what this costs: committed-versus-goal is now a
    // LUMINANCE and SATURATION distinction, not a hue one. That is the
    // first thing a global brightness change eats, and this board has
    // already had one such change. It is measured against the legend for
    // that reason.
    const mobActive = shared('mobActive', () => new THREE.MeshStandardMaterial({
      color: '#e8dcbb', roughness: 0.5, metalness: 0,
    }));
    const mobGoal = shared('mobGoal', () => new THREE.MeshStandardMaterial({
      color: '#c9a14a', roughness: 0.45, metalness: 0,
    }));
    // The SAME orange as the engagement lines to unplanned work, so a
    // piece and its lines read as one thing: unplanned is one colour
    // everywhere.
    //
    // The previous dull grey was only 70 from the committed grey in
    // material space, and 41 on screen — measured — so a reinforcement
    // and a committed ticket were hard to tell apart, which is the whole
    // job of this colour. Now 97 measured on screen.
    //
    // The body is lit and the lines are not, so this renders darker than
    // the lines do. Same hue, which is what matters.
    const mobReinforce = shared('mobReinforce', () => new THREE.MeshStandardMaterial({
      color: UNPLANNED_SOLID_COLOUR, roughness: 0.5, metalness: 0,
    }));
    // Ground ring marking a boss minion. Flat, so it reads as painted
    // on the field rather than as another object. Deliberately not a
    // body colour: colour carries goal membership, and one channel
    // per meaning is the rule that keeps this readable.
    // THE DOME OVER BLOCKED WORK.
    //
    // Operator: "if a ticket becomes blocked, it should get a dome shield
    // over it, semi transparent."
    //
    // Blocked is the one status with no mark at all since the sword and the
    // shield went: it keeps every horn, because a beast standing still is not
    // a beast dying, and no colour or depth says "stopped" without saying
    // something else too. So it gets a SHAPE, which is the channel this board
    // uses for status.
    //
    // A hemisphere: the top half of a sphere, so it sits ON the ground rather
    // than through it. One geometry, scaled per piece — a blocked piece is
    // still one mesh more, not one geometry more.
    const domeGeo = shared('domeGeo', () => new THREE.SphereGeometry(
      1, 16, 8, 0, Math.PI * 2, 0, Math.PI / 2,
    ));
    const domeMat = shared('domeMat', () => new THREE.MeshStandardMaterial({
      color: '#9cc2e8',
      transparent: true,
      // 0.35, up from 0.22. Operator: "a little more wide, and less
      // transparent." Below about a third it reads as a tint on the
      // background rather than as an object; above about a half the piece
      // inside stops being visible, which defeats the point of a see-through
      // dome.
      opacity: 0.35,
      // DoubleSide so the inside of the dome is drawn too — a single-sided
      // shell over a piece reads as a hole from some angles, because you are
      // looking at its back face.
      side: THREE.DoubleSide,
      // depthWrite off. A transparent surface that writes depth hides
      // whatever is behind it, and there is always a piece behind this one.
      depthWrite: false,
      roughness: 0.35,
      metalness: 0.1,
    }));
    const ringGeo = shared('ringGeo', () => new THREE.RingGeometry(0.78, 1.02, 20));
    const ringMat = shared('ringMat', () => new THREE.MeshBasicMaterial({
      color: '#d8455a', side: THREE.DoubleSide, transparent: true, opacity: 0.9,
    }));
    // Staleness is counted but not drawn. The floating beacons read as
    // debris rather than signal, so they're gone for now — the count
    // survives in the footer, where it costs nothing to read.
    const buildNow = Date.now();
    let staleCount = 0;
    for (const m of allPieces) {
      // A kill we already animated counts as cleared until the data
      // catches up, otherwise the piece pops back to life on the next
      // rebuild.
      // Cleared work is filtered out of allPieces, so this only catches
      // a piece killed this session whose data has not caught up yet.
      const cleared = m.cleared || killedLocallyRef.current.has(m.key);
      if (cleared) continue;
      const isMinion = m.minionBossId !== null;
      const inGoal = m.goalId !== null;
      const mesh = new THREE.Mesh(
        beastGeoFor(m.appendages),
        m.isReinforcement ? mobReinforce
          : inGoal ? mobGoal : mobActive,
      );
      // Minions stay slightly larger — a second, independent cue that
      // survives a colour-blind read.
      const base = cleared ? CLEARED_SIZE : isMinion ? MOB_SIZE * 1.25 : MOB_SIZE;
      const scale = cleared ? 1 : base / MOB_SIZE;
      mesh.scale.setScalar(scale);
      // The piece's height AFTER its scale, which is what decides where its
      // feet are. `base` is still the size constant the scale is derived
      // from; it is not the height, and using it as one is what buried the
      // mobs.
      const pieceHeight = cleared ? TOMB_H : beastHeightFor(m.appendages) * scale;
      // groundY is the surface (platform for goal mobs); y is the
      // stack lift for pieces sharing a grid cell — see staggerStack
      // in layout.ts. A headstone stands on the surface, so its own
      // half-height is what lifts it.
      // A PIECE DOES NOT SINK. It used to be buried in the ground by its
      // progress toward done, because an indicator drawn beside a piece
      // would overlap its neighbours: one piece is 8 screen px wide and the
      // closest two are 3.5 px apart. Sinking was how progress was read in
      // the silhouette instead.
      //
      // Status is now carried by the HORNS - see features/battlemap/beast.ts
      // - and two channels for one meaning is exactly the mistake this board
      // keeps having to correct. Worse, they contradicted each other: a
      // sinking beast loses height as it advances while a losing its horns
      // loses height too, so the two would have doubled up and buried a
      // Testing piece in the ground for no reason at all.
      //
      // So the horns carry it alone, and every piece stands at full height.
      const restY = m.groundY + pieceHeight / 2 + m.y;
      mesh.position.set(m.x, restY, m.z);
      // Remembered so the kill/flinch animation restores to the SUNKEN
      // position rather than snapping the piece back up to full height.
      mesh.userData.restY = restY;
      // Headstones all face the same way; a grave marker turned to a
      // random angle reads as debris rather than as a marker.
      mesh.rotation.y = cleared ? 0 : (m.key.length * 0.7) % Math.PI;
      mesh.userData.mobKey = m.key;
      mesh.castShadow = shadows;
      mesh.receiveShadow = shadows;
      scene.add(mesh);
      // `base` here is the piece's real height, so the flinch and kill
      // animations land on the ground instead of half a unit through it,
      // and the engagement marks sit above the horns rather than inside
      // them.
      if (!cleared) mobMeshesRef.current.set(m.key, { mesh, base: pieceHeight, lift: m.y + m.groundY });
      // Blocked: a dome over the piece.
      //
      // Sized to the piece's REAL height, like the feet are — the beast's
      // height depends on its horns, and a dome sized from MOB_SIZE would sit
      // buried inside a fresh beast exactly the way the pieces themselves
      // used to. It reaches a little above the crown so the horns are inside
      // it rather than through it.
      //
      // Narrower than a sphere of that height, because a full sphere tall
      // enough to cover a five-horn beast would be twice as wide as the
      // piece and reach into its neighbours on a crowded field.
      if (m.blocked && !cleared) {
        // A little bigger, to ENCLOSE the piece rather than hug it.
        //
        // Operator: "make it a little bigger, so it encloses the mob."
        //
        // 1.20 radius. It was 0.85, then 1.05 — "a little more wide" — and
        // the beast's body is about 0.94, so this now clears it by better
        // than a quarter of its own width on each side.
        //
        // Worth knowing on a crowded field: pieces are 8 screen px wide and
        // the closest two are 3.5 apart, so two blocked tickets side by side
        // will have domes that overlap each other. That is the cost of
        // enclosing a piece whose neighbours are closer than its own width.
        const domeH = pieceHeight + 0.55;
        const dome = new THREE.Mesh(domeGeo, domeMat);
        dome.scale.set(1.20, domeH, 1.20);
        dome.position.set(m.x, m.groundY + m.y, m.z);
        dome.userData.mobKey = m.key;
        scene.add(dome);
      }

      // NO MARKERS. The sword and the shield were a status bit bolted on
      // in front of the piece. Status is now the SHAPE of the piece itself -
      // see features/battlemap/beast.ts - which is one object instead of
      // two, and legible at a glance instead of requiring the legend.

      // Boss minion: a crimson ring under the piece, on whatever
      // surface it stands on. Own channel, so it can coexist with the
      // body colour.
      if (!cleared && isMinion) {
        const ring = new THREE.Mesh(ringGeo, ringMat);
        ring.rotation.x = -Math.PI / 2;
        ring.position.set(m.x, m.groundY + 0.06 + m.y, m.z);
        ring.scale.setScalar(base / MOB_SIZE);
        scene.add(ring);
      }
      // Counted, not drawn — see the note above.
      if (!cleared && isStale(m.lastTouchAt, buildNow, staleDays)) {
        staleCount += 1;
      }
    }

    setStaleCount(staleCount);

    // --- SELECTION --------------------------------------------------
    // One ring, moved rather than rebuilt, so click-to-inspect is a
    // property change and not a scene change. It has to be its own
    // object: the three mob materials are SHARED across every piece,
    // so highlighting by tinting the selected mesh would tint all of
    // them.
    //
    // Sized to clear a minion piece (the largest kind) rather than
    // scaled per piece, because a ring that changed size with the
    // selection would read as a different signal each time.
    const selGeo = shared('selGeo', () => new THREE.RingGeometry(1.05, 1.35, 28));
    const selMat = shared('selMat', () => new THREE.MeshBasicMaterial({
      color: '#ffffff', side: THREE.DoubleSide, transparent: true, opacity: 0.85,
    }));
    const selRing = new THREE.Mesh(selGeo, selMat);
    selRing.rotation.x = -Math.PI / 2;
    selRing.visible = false;
    scene.add(selRing);
    selectionRingRef.current = selRing;
    // Positioned here as well as in the effect below, so a rebuild
    // does not leave the ring behind for a frame.
    placeSelectionRing(selRing, allPieces, selectedMobRef.current);

    // --- GOAL ENCAMPMENTS -------------------------------------------
    // A raised platform per goal, plus posts showing cleared/total
    // as literal height so progress is readable from the camera.
    for (const g of field.goals) {
      // The platform is the only thing marking a camp now that the
      // progress posts are gone — mobs standing on a raised pad, or an
      // empty pad, which is itself the 'done' signal.
      // A fence around the camp, not a raised platform.
      //
      // The platform had height, and height hides the bottom of anything
      // standing on it — which, now that a piece sinks as it nears done,
      // hides precisely the thing the sinking exists to show. A fence
      // marks the same boundary and leaves the interior flat, so pieces
      // inside it stand and sink on the shared ground like any other.
      //
      // Built as one extruded ring: the outline with an inset hole,
      // pulled up to a low wall. One mesh per camp, one draw call.
      {
        const HX = 11;
        const HZ = 6.5;
        const T = 0.22;
        const outline = new THREE.Shape();
        outline.moveTo(-HX, -HZ);
        outline.lineTo(HX, -HZ);
        outline.lineTo(HX, HZ);
        outline.lineTo(-HX, HZ);
        outline.closePath();
        const inner = new THREE.Path();
        inner.moveTo(-HX + T, -HZ + T);
        inner.lineTo(HX - T, -HZ + T);
        inner.lineTo(HX - T, HZ - T);
        inner.lineTo(-HX + T, HZ - T);
        inner.closePath();
        outline.holes.push(inner);
        const geo = shared('goalCampGeo', () => new THREE.ExtrudeGeometry(outline, {
          depth: 0.34, bevelEnabled: false,
        }));
        const mat = shared('goalCampMat', () => new THREE.MeshStandardMaterial({
          color: '#6e5a2c', roughness: 0.45, metalness: 0.75,
        }));
        const fence = new THREE.Mesh(geo, mat);
        // The extrusion runs +Z, which this rotation turns into +Y, so
        // the wall rises out of the ground rather than lying in it.
        fence.rotation.x = -Math.PI / 2;
        fence.position.set(g.x, 0.02, g.z);
        fence.castShadow = shadows;
        fence.receiveShadow = shadows;
        scene.add(fence);
      }

      // Orphan keys (in the goal but not committed) render faded —
      // a config mistake the operator should be able to spot.
      g.orphanKeys.forEach((k, i) => {
        const om = shared('orphanMat', () => new THREE.MeshStandardMaterial({
          color: '#5a3a3a', roughness: 0.8, metalness: 0,
        }));
        const o = new THREE.Mesh(mobGeo, om);
        o.scale.setScalar(CLEARED_SIZE / MOB_SIZE);
        o.position.set(g.x + 5 + i * 1.6, CLEARED_SIZE / 2, g.z + 5);
        o.castShadow = shadows;
        scene.add(o);
      });
    }

    // --- BOSSES -----------------------------------------------------
    const bossGeo = shared('bossGeo', () => new THREE.BoxGeometry(1, 1, 1));
    for (const b of field.bosses) {
      const frac = b.hpMax > 0 ? b.hpRemaining / b.hpMax : 0;
      const h = 6 + frac * 18;
      const mat = frac > 0.5
        ? shared('bossMatHigh', () => new THREE.MeshStandardMaterial({
            color: '#7a2020', roughness: 0.85, metalness: 0,
          }))
        : shared('bossMatLow', () => new THREE.MeshStandardMaterial({
            color: '#a83232', roughness: 0.85, metalness: 0,
          }));
      const w = BOSS_SIZE * 2;

      // A KEEP, not a stretched cube.
      //
      // Measured, this is the largest object on the board — 29 to 117
      // screen px tall and 39 px wide at the default camera — and it was
      // BoxGeometry(1,1,1) scaled. It is also the anchor of the whole
      // depth ordering: the enemy at the far end, the thing that makes
      // "far" mean "dangerous". It was the one place on the board with
      // room for a silhouette, and it was spending that room on a box.
      //
      // Three pieces rather than one: a hall with a tower at each front
      // corner, standing proud of it. At 39px wide the towers are about
      // 8px each, which is enough to break the rectangle into something
      // that reads as a building. Cheap — six more meshes for the whole
      // board, not six per boss.
      const hall = new THREE.Mesh(bossGeo, mat);
      hall.scale.set(w, h, w);
      hall.position.set(b.x, h / 2, b.z);
      hall.castShadow = shadows;
      hall.receiveShadow = shadows;
      scene.add(hall);

      const towerH = h * 1.28;
      const towerW = w * 0.3;
      for (const side of [-1, 1]) {
        const tower = new THREE.Mesh(bossGeo, mat);
        tower.scale.set(towerW, towerH, towerW);
        // Standing proud toward the viewer, so the front face shows a
        // stepped outline rather than a flat wall.
        tower.position.set(b.x + side * (w / 2 - towerW / 2), towerH / 2, b.z + w * 0.22);
        tower.castShadow = shadows;
        tower.receiveShadow = shadows;
        scene.add(tower);
      }
    }

    // --- HEROES -----------------------------------------------------
    // Cones so they read differently from mobs at a glance, with an
    // HTML name label floating above each one.
    // 20 segments, up from 8. A hero cone can be clicked and the camera can
    // close to 25 units, where eight flat faces are plainly visible.
    // A FIGURE, not a cone.
    //
    // A cone is a marker: it says "something is here". A hero is a PERSON
    // — their name floats above it and you click it to see what they own
    // — and a traffic cone is the wrong thing to put under a name.
    //
    // This is a lathe-turned miniature: a base, a body tapering to the
    // shoulders, a neck, a head. Same height as the cone it replaces, so
    // the label height, the selection scale and the click raycast all keep
    // working untouched.
    //
    // Deliberately a solid of revolution, unlike the sword and shield
    // markers. Those are flat plates, which is right for equipment lying
    // in front of a piece; a figure has to read from whatever angle the
    // board is being orbited to, and a flat plate at 90 degrees is a line.
    const heroGeo = shared('heroGeo', buildHeroGeometry);
    const heroMeshes: THREE.Mesh[] = [];
    for (const h of field.heroes) {
      const heroColor = heroColorFor(h.id);
      // The heroes are the METAL: cast miniatures rather than painted
      // ones, which is the reason the environment map exists.
      //
      // 0.5, not 0.9. At 0.9 they rendered as mirrors of the environment
      // and lost their chip colour — and that colour is how you tell one
      // person from another. Metal enough to gleam, base colour still
      // doing its job.
      const mat = shared(`hero-${heroColor}`, () => new THREE.MeshStandardMaterial({
        color: heroColor, roughness: 0.34, metalness: 0.5,
      }));
      const mesh = new THREE.Mesh(heroGeo, mat);
      mesh.position.set(h.x, HERO_SIZE * 1.2, h.z);
      // Used by the click raycaster to work out which cone was hit.
      mesh.userData.heroId = h.id;
      mesh.userData.baseY = HERO_SIZE * 1.2;
      mesh.castShadow = shadows;
      scene.add(mesh);
      heroMeshes.push(mesh);
    }

    // --- SELECTION + TETHERS ---------------------------------------
    // Tethers used to be always-on, which was unreadable: 16 heroes
    // and ~40 owned tickets produced a web. They now appear only for
    // the hero you click, which is also when you actually want to
    // know who owns what.
    const tetherGroup = new THREE.Group();
    scene.add(tetherGroup);

    // Dot clouds rather than dashed lines. A dash is a static artefact;
    // dots that march read as attention flowing from the hero to the
    // work, which is the thing the tether is for.
    const dotTexture = shared('dotTexture', () => createDotTexture());
    let tetherDots: Array<{ points: THREE.Points; geo: THREE.BufferGeometry; from: Anchor; to: Anchor; count: number }> = [];
    // Holds both kinds now: a LineBasicMaterial for ownership and a
    // PointsMaterial for assistance.
    let tetherMats: THREE.Material[] = [];

    const clearTethers = () => {
      for (const child of [...tetherGroup.children]) {
        tetherGroup.remove(child);
        (child as THREE.Line | THREE.Points).geometry?.dispose();
      }
      tetherDots = [];
      for (const m of tetherMats) m.dispose();
      tetherMats = [];
    };

    /** Animated dots along one ASSISTANCE line.
     *
     *  Ownership does not use this. A structural fact — this ticket is
     *  yours — should be legible without drawing the eye, and motion is
     *  what draws the eye. So ownership is a thin static line and the
     *  march is reserved for help.
     */
    const addTether = (
      from: Anchor, to: Anchor, material: THREE.PointsMaterial, densityScale = 1,
    ) => {
      const length = Math.hypot(to.x - from.x, to.y - from.y, to.z - from.z);
      const count = Math.min(26, Math.max(4, Math.round((length / 1.5) * densityScale)));
      const arr = new Float32Array(count * 3);
      const geo = new THREE.BufferGeometry();
      geo.setAttribute('position', new THREE.BufferAttribute(arr, 3));
      dotPositions(from, to, count, 0, arr);
      const points = new THREE.Points(geo, material);
      tetherGroup.add(points);
      tetherDots.push({ points, geo, from, to, count });
    };

    /** Draw the engagement lines for whatever is selected.
     *
     *  Two directions, one routine. Selecting a hero shows the tickets
     *  they are accountable for and the ones they helped with; selecting
     *  a ticket shows the hero accountable for it and everyone who helped.
     *  Those are the same relationship read from opposite ends, so they
     *  are built from a single list of what to draw rather than two
     *  branches — two branches would be free to drift.
     */
    const refreshTethers = () => {
      clearTethers();
      const selected = selectedHeroRef.current;
      // Selection is exclusive, so at most one of these is set.
      const selectedTicket = selectedMobRef.current;

      // Highlight: the picked hero stands taller. Runs in both modes so
      // switching selection restores the previous hero's size.
      for (const mesh of heroMeshes) {
        const on = mesh.userData.heroId === selected;
        mesh.scale.setScalar(on ? 1.35 : 1);
        mesh.position.y = on ? (mesh.userData.baseY as number) * 1.35 : (mesh.userData.baseY as number);
      }
      if (!selected && !selectedTicket) return;

      const stand = new Map(allPieces.map((m) => [m.key, m]));
      const mobAnchorFor = (key: string) => {
        const m = stand.get(key);
        if (!m) return null;
        const entry = mobMeshesRef.current.get(key);
        return { x: m.x, z: m.z, groundY: m.groundY, base: entry?.base ?? MOB_SIZE, lift: m.y };
      };
      const heroAnchorFor = (heroId: string) => {
        const h = field.heroes.find((x) => x.id === heroId);
        // A hero only exists for someone who owns standing work, so an
        // actor off the board has nowhere to draw a line from. Skipped
        // rather than invented a position for.
        if (!h) return null;
        const mesh = heroMeshes.find((x) => x.userData.heroId === heroId);
        return {
          x: h.x,
          z: h.z,
          baseY: (mesh?.position.y as number | undefined) ?? HERO_HALF_HEIGHT,
          scale: mesh?.scale.y ?? 1,
        };
      };

      // --- what to draw ---------------------------------------------
      // Each entry carries whether its target is unplanned, because
      // that is what the COLOUR encodes. The solid/dotted split encodes
      // the relationship. Neither channel does both jobs.
      interface Planned { hero: string; mob: string; unplanned: boolean }
      const solid: Planned[] = [];
      const dotted: Planned[] = [];
      const isUnplanned = (key: string) => stand.get(key)?.isReinforcement === true;

      if (selectedTicket) {
        if (!stand.has(selectedTicket)) return;
        const unplanned = isUnplanned(selectedTicket);
        const plan = ticketTetherPlan(selectedTicket, assigneeOf, actorsOf);
        if (plan.accountable) solid.push({ hero: plan.accountable, mob: selectedTicket, unplanned });
        for (const heroId of plan.assisting.slice(0, ASSIST_CAP)) {
          dotted.push({ hero: heroId, mob: selectedTicket, unplanned });
        }
      } else if (selected) {
        const standing = allPieces.filter((m) => !m.cleared).map((m) => m.key);
        const plan = tetherPlan(selected, standing, assigneeOf, actorsOf);
        for (const key of plan.ownership) {
          solid.push({ hero: selected, mob: key, unplanned: isUnplanned(key) });
        }
        for (const key of plan.assistance.slice(0, ASSIST_CAP)) {
          dotted.push({ hero: selected, mob: key, unplanned: isUnplanned(key) });
        }
      }

      if (solid.length === 0 && dotted.length === 0) return;

      // --- and how --------------------------------------------------
      // Materials are keyed by colour and built on demand, so only the
      // colours actually in use are allocated. One solid material used
      // to be built from the selected hero's colour; a selection can now
      // draw in two at once.
      const lineMats = new Map<string, THREE.LineBasicMaterial>();
      const lineMatFor = (colour: string) => {
        let m = lineMats.get(colour);
        if (!m) {
          m = new THREE.LineBasicMaterial({ color: colour, transparent: true, opacity: 0.85 });
          tetherMats.push(m);
          lineMats.set(colour, m);
        }
        return m;
      };
      const dotMats = new Map<string, THREE.PointsMaterial>();
      const dotMatFor = (colour: string) => {
        let m = dotMats.get(colour);
        if (!m) {
          m = new THREE.PointsMaterial({
            color: colour,
            map: dotTexture,
            // CSS pixels. three accounts for the device pixel ratio
            // already, so multiplying by it here doubled the apparent
            // size on a HiDPI display - see DEFAULT_DOT_PX.
            size: dotPx,
            sizeAttenuation: false,
            transparent: true,
            opacity: 0.85,
            // Transparent points must not write depth, or they punch
            // holes in whatever is drawn after them.
            depthWrite: false,
          });
          tetherMats.push(m);
          dotMats.set(colour, m);
        }
        return m;
      };

      // Ownership: a thin SOLID line, and deliberately static. WebGL
      // ignores LineBasicMaterial.linewidth, so this is a hairline
      // whatever the machine — which is the "thin" that was asked for.
      for (const p of solid) {
        const a = heroAnchorFor(p.hero);
        const b = mobAnchorFor(p.mob);
        if (!a || !b) continue;
        const [from, to] = tetherAnchors(a, b);
        const geo = new THREE.BufferGeometry().setFromPoints([
          new THREE.Vector3(from.x, from.y, from.z),
          new THREE.Vector3(to.x, to.y, to.z),
        ]);
        tetherGroup.add(new THREE.Line(
          geo,
          lineMatFor(tetherColour('assigned', p.unplanned, heroColorFor(p.hero))),
        ));
      }
      for (const p of dotted) {
        const a = heroAnchorFor(p.hero);
        const b = mobAnchorFor(p.mob);
        if (!a || !b) continue;
        const [from, to] = tetherAnchors(a, b);
        addTether(from, to, dotMatFor(
          tetherColour('assisting', p.unplanned, heroColorFor(p.hero)),
        ), 0.75);
      }
    };

    refreshTethersRef.current = refreshTethers;
    refreshTethers();

    // Where a piece and a hero are ON SCREEN, for the event marks.
    //
    // THIS HAS NOW BEEN DELETED TWICE, both times by a slice that ran from a
    // "TEMPORARY" marker to the next landmark and took these lines with it.
    // The symptom is silent: the playhead advances, the board moves, and no
    // marks are drawn, because every ping resolves to "no position". There
    // is no error and nothing to see. The second deletion happened AFTER a
    // comment warning about the first one, which is why there is now a
    // warning in firePings as well — a comment is not a guard.
    //
    // They are published here because the mark is drawn in React, outside
    // this effect's closure, and it cannot reach the scene.
    heroScreenRef.current = (id: string) => {
      const mesh = heroMeshes.find((h) => h.userData.heroId === id);
      if (!mesh || !mesh.visible) return null;
      const v = mesh.position.clone();
      v.y += HERO_SIZE * 1.4;
      v.project(camera);
      if (v.z > 1) return null;
      const rect = renderer.domElement.getBoundingClientRect();
      return {
        x: rect.left + (v.x * 0.5 + 0.5) * rect.width,
        y: rect.top + (-v.y * 0.5 + 0.5) * rect.height,
      };
    };

    pieceScreenRef.current = (key: string) => {
      const entry = mobMeshesRef.current.get(key);
      if (!entry || !entry.mesh.visible) return null;
      const v = entry.mesh.position.clone();
      v.y += entry.base * 0.9;
      v.project(camera);
      if (v.z > 1) return null;
      const rect = renderer.domElement.getBoundingClientRect();
      return {
        x: rect.left + (v.x * 0.5 + 0.5) * rect.width,
        y: rect.top + (-v.y * 0.5 + 0.5) * rect.height,
      };
    };


    // Click a hero to select. Click anywhere else to clear.
    // --- SELECTION BY CLICK, NOT BY TOUCH ---------------------------
    // This used to act on pointerdown, which meant anything that BEGAN
    // with a press cleared the selection — including the press that
    // starts an orbit. Moving the board wiped the lines you were
    // looking at.
    //
    // So: remember where the press landed, and only treat it as a click
    // if the pointer came up in nearly the same place. Anything that
    // travelled is a drag, and a drag is the operator moving the camera,
    // which must not touch the selection.
    const raycaster = new THREE.Raycaster();
    /** Pixels of travel still counted as a click. Generous enough for a
     *  shaky hand on a trackpad, tight enough that an orbit never
     *  qualifies. */
    const DRAG_SLOP = 5;
    let pressAt: { x: number; y: number } | null = null;

    const onPointerDown = (ev: MouseEvent) => {
      pressAt = { x: ev.clientX, y: ev.clientY };
    };

    const onPointerUp = (ev: MouseEvent) => {
      const press = pressAt;
      pressAt = null;
      if (!press) return;
      if (Math.hypot(ev.clientX - press.x, ev.clientY - press.y) > DRAG_SLOP) return;

      const rect = renderer.domElement.getBoundingClientRect();
      const ndc = new THREE.Vector2(
        ((ev.clientX - rect.left) / rect.width) * 2 - 1,
        -((ev.clientY - rect.top) / rect.height) * 2 + 1,
      );
      raycaster.setFromCamera(ndc, camera);
      // Both kinds in one cast, nearest wins. Testing them separately
      // would let a piece behind a hero cone steal the click.
      const mobMeshes = [...mobMeshesRef.current.values()].map((v) => v.mesh);
      const hit = raycaster.intersectObjects([...mobMeshes, ...heroMeshes], false)[0];
      const mobKey = hit && hit.object.userData.mobKey ? (hit.object.userData.mobKey as string) : null;
      const heroId = hit && hit.object.userData.heroId ? (hit.object.userData.heroId as string) : null;

      // Clicking empty ground changes nothing. The only ways out of a
      // selection are clicking the same thing again, or clicking a
      // different one — so a stray click while reading never costs you
      // the lines you were reading.
      if (!mobKey && !heroId) return;

      // One selection at a time: inspecting a ticket and inspecting a
      // person are different questions, and two open panels would
      // compete for the same corner. Clicking what is already selected
      // puts it down again.
      const nextMob = mobKey && selectedMobRef.current !== mobKey ? mobKey : null;
      const nextHero = heroId && selectedHeroRef.current !== heroId ? heroId : null;
      selectedMobRef.current = nextMob;
      selectedHeroRef.current = nextMob ? null : nextHero;
      // The React state change drives a separate effect that calls
      // refreshTethersRef — one path for both click and programmatic
      // selection, so they can't drift.
      onMobSelectionChange(nextMob);
      onSelectionChange(nextMob ? null : nextHero);
    };

    renderer.domElement.addEventListener('pointerdown', onPointerDown);
    renderer.domElement.addEventListener('pointerup', onPointerUp);

    // --- LABELS -----------------------------------------------------
    // HTML labels rather than 3D text: crisp at any distance, free to
    // style, and no font geometry to load. Positioned by projecting
    // each world position into screen space every frame.
    //
    // Heroes and goal encampments both get one. Goals need it more
    // than the heroes do — nothing else on the board says which camp
    // is which.
    const labelLayer = document.createElement('div');
    labelLayer.style.cssText =
      'position:absolute;inset:0;overflow:hidden;pointer-events:none;';
    mount.appendChild(labelLayer);
    // THREE EMPHASES. 'name' is a person and hides until clicked; 'place' is
    // a camp, set above it; 'base' is a keep, set at its foot and smaller.
    //
    // The vertical version was tried first and the operator rejected it —
    // "no that doesn't work, write it at the base of the structure, but
    // shrink the font". So: horizontal again, at the base, in a smaller face.
    //
    // The anchor is carried on the record rather than hard-coded in
    // updateLabels, because the three sit differently: a person's name is
    // centred above them, a camp's above it, a keep's centred on its foot.
    const makeLabel = (text: string, emphasis: 'name' | 'place' | 'base') => {
      const el = document.createElement('div');
      el.textContent = text;
      // A truncated name is still recoverable: hover shows the whole thing.
      if (emphasis !== 'name') el.title = text;
      el.style.cssText = [
        'position:absolute;left:0;top:0',
        'transform:translate(-50%,-100%)',
        'padding:1px 5px',
        'line-height:1.3',
        'white-space:nowrap',
        'border-radius:5px',
        'will-change:transform',
        // A name that appears and vanishes at four events a second, with no
        // fade, strobes the field. 160ms is long enough to read as a fade and
        // short enough that the name is gone well before the next event.
        'transition:opacity 160ms ease-out',
        // START HIDDEN, and this is not decoration.
        //
        // The scene REBUILDS on every event during a replay, so every label
        // is a fresh element several times a second. A fresh element starts at
        // the CSS default opacity of 1, stays visible until updateLabels
        // first runs, and is then hidden. That was a one-frame flash, which
        // nobody saw; adding the transition above turned it into a 160ms fade
        // in and out of every name on the field, several times a second.
        // Which is what "all the names are flickering" was.
        //
        // So a name label begins invisible and is only ever shown
        // deliberately. Place and base labels are landmarks and begin
        // visible, because they are never hidden.
        emphasis === 'name' ? 'opacity:0' : 'opacity:1',
        'background:rgba(6,8,11,0.72)',
        'color:rgba(255,255,255,0.78)',
        // Places read as headings (uppercase, tracked); hero names
        // read as people.
        // A CAP ON PLACE NAMES, because they are longer than the gap between
        // the things they name.
        //
        // Measured: the boss band gives 29 units between keeps at the default
        // camera, which is about 179 screen px, and the longest epic name is
        // 57 characters — roughly 340px. Nine of those across one band went
        // through each other, which is what the operator saw.
        //
        // Truncated rather than shortened, so the full name survives wherever
        // there is room for it: a board with two bosses, or a camera zoomed
        // in, still reads the whole thing. Only a crowd ellipsises.
        emphasis === 'place'
          ? 'font-size:10px;font-weight:600;letter-spacing:0.14em;text-transform:uppercase;color:rgba(245,214,140,0.92);max-width:190px;overflow:hidden;text-overflow:ellipsis'
          : emphasis === 'base'
            // SMALLER than a camp, and less tracked.
            //
            // 8px against 10, and letter-spacing down from 0.14em to 0.08em.
            // The tracking that makes a camp name read as a heading stops a
            // small one being legible at all — uppercase letters spaced apart
            // at 8px stop being a word and become a row of marks.
            //
            // It can be smaller than a camp because it sits at the FOOT of
            // the largest object on the board rather than floating in open
            // sky, so it has something to read against.
            ? 'font-size:8px;font-weight:600;letter-spacing:0.08em;text-transform:uppercase;color:rgba(245,214,140,0.92);max-width:150px;overflow:hidden;text-overflow:ellipsis'
            : 'font-size:11px;font-weight:500',
      ].join(';');
      labelLayer.appendChild(el);
      return el;
    };
    const labels = [
      ...field.heroes.map((h) => ({
        id: h.id,
        x: h.x,
        z: h.z,
        y: HERO_SIZE * 2.4 + 1.2,
        el: makeLabel(heroNameFor(h.id), 'name' as const),
        emphasis: 'name' as const,
        anchor: 'translate(-50%,-100%)',
      })),
      ...field.goals.map((g) => ({
        id: g.id,
        x: g.x,
        z: g.z,
        y: PLATFORM_TOP + 5.5,
        el: makeLabel(g.name, 'place' as const),
        emphasis: 'place' as const,
        anchor: 'translate(-50%,-100%)',
      })),
      // BOSSES get a name too, and they are the ones that most needed one:
      // a keep is the largest object on the board and the anchor of the
      // whole depth ordering, and it was the only landmark on it with no
      // label. A player could see that a fortress was there and not which
      // epic it stood for.
      //
      // 'place' emphasis, like the camps — a keep is a landmark that never
      // moves, not a person. So it is always visible rather than only on
      // click, which is the opposite of what hero names do now.
      //
      // At the BASE now, so the height no longer matters — which is one
      // thing the vertical attempt got right and this keeps: a label whose
      // position depends on the keep's health moves every time the keep
      // changes size, and the foot of a thing does not move.
      ...field.bosses.map((b) => {
        return {
          id: `boss:${b.id}`,
          // The FOOT of the keep: its own centre, at ground level.
          x: b.x,
          z: b.z,
          y: 0,
          el: makeLabel(b.name, 'base' as const),
          emphasis: 'base' as const,
          // Centred on the foot of the keep, and sitting just below it, so it
          // reads as a nameplate cut into the base rather than a caption
          // floating over the wall.
          anchor: 'translate(-50%,0)',
        };
      }),
    ];
    const projectV = new THREE.Vector3();
    const updateLabels = () => {
      const w = mount.clientWidth;
      const hgt = mount.clientHeight;
      for (const l of labels) {
        projectV.set(l.x, l.y, l.z);
        const cam = projectV.clone().applyMatrix4(camera.matrixWorldInverse);
        if (cam.z > -1) {
          // Behind the camera — projection would wrap it to the far
          // side of the screen.
          l.el.style.opacity = '0';
          continue;
        }
        projectV.project(camera);
        const sx = (projectV.x * 0.5 + 0.5) * w;
        const sy = (-projectV.y * 0.5 + 0.5) * hgt;
        l.el.style.transform = `${l.anchor} translate(${sx}px, ${sy}px)`;
        if (l.emphasis === 'name') {
          // A HERO'S NAME IS SHOWN ONLY WHEN THAT HERO IS SELECTED.
          //
          // Twelve names floating over the field is a wall of text that
          // competes with the pieces for attention, and the operator reads
          // the board by SHAPE and COLOUR long before they read a name.
          // Clicking a hero is already how you ask "who is this" - the
          // footer says so - so the name belongs to the answer, not to the
          // field.
          //
          // The place labels are different: there are a handful of camps,
          // they never move, and their names are the board's landmarks.
          const named = pingNamedRef.current;
          const on = selectedHeroRef.current === l.id
            || (named.id === l.id && named.until > performance.now());
          l.el.style.opacity = on ? '1' : '0';
          l.el.style.color = '#ffffff';
          l.el.style.fontWeight = '700';
          l.el.style.background = 'rgba(20,26,34,0.92)';
        } else {
          l.el.style.opacity = '1';
        }
      }
    };

    // POSITION THEM IN THE SAME TICK THEY ARE CREATED.
    //
    // updateLabels otherwise only runs from the render loop, on the NEXT
    // frame. A label created here therefore sits at its initial opacity for up
    // to 16ms — invisible — and since a replay rebuilds the scene four times a
    // second, a half-second flash was four one-frame blinks rather than one
    // continuous appearance. Calling it once here closes that window: the
    // element is created, positioned and given its final opacity before the
    // browser ever paints it.
    updateLabels();

    // --- THE GRAVEYARD (side quests, closed) ------------------------
    if (showGraveyard) {
      // Every grave is closed by definition - the query requires a
      // terminal status - so they are all headstones.
      const wildMat = shared('wildMat', () => new THREE.MeshStandardMaterial({
        color: '#4a5a3a', roughness: 0.92, metalness: 0,
      }));
      for (const w of field.graveyard) {
        const mesh = new THREE.Mesh(tombGeo, wildMat);
        mesh.position.set(w.x, TOMB_H / 2, w.z);
        mesh.castShadow = shadows;
        mesh.receiveShadow = shadows;
        scene.add(mesh);
      }
    }

    let raf = 0;
    let last = performance.now();
    let frames = 0;
    let acc = 0;
    const animate = () => {
      raf = requestAnimationFrame(animate);
      const now = performance.now();
      const dt = now - last;
      last = now;
      acc += dt;
      frames++;
      if (acc >= 500) {
        // Report what the operator actually needs to judge: frame
        // rate and scene cost.
        setStats({
          fps: Math.round((frames * 1000) / acc),
          drawCalls: renderer.info.render.calls,
          tris: renderer.info.render.triangles,
        });
        setCombat({ ...combatRef.current });
        frames = 0;
        acc = 0;
      }
      stepAnims(now);
      stepTetherDots(now);
      updateLabels();
      controls.update();
      renderer.render(scene, camera);
    };

    const sparkGeo = new THREE.BoxGeometry(0.22, 2.2, 0.22);

    /** March the engagement dots. One cycle from hero to ticket every
     *  dotCycleMs; rewriting a few hundred floats a frame is free at
     *  this scale, and it avoids a shader for the sake of a shader. */
    function stepTetherDots(now: number) {
      if (tetherDots.length === 0) return;
      const phase = (now % dotCycleMs) / dotCycleMs;
      for (const d of tetherDots) {
        const attr = d.geo.attributes.position;
        if (!attr) continue;
        dotPositions(d.from, d.to, d.count, phase, attr.array as Float32Array);
        attr.needsUpdate = true;
      }
    }

    /** Pop queued events into animations, and advance the live ones. */
    function stepAnims(now: number) {
      // Start queued animations. Cap concurrency so a burst at sprint
      // end doesn't turn the board into a strobe.
      const queue = animQueueRef.current;
      while (queue.length > 0 && animsRef.current.length < 6) {
        const next = queue.shift()!;
        const entry = mobMeshesRef.current.get(next.key);
        if (!entry) continue; // not on the map (uncommitted, or already down)
        const sparkMat = new THREE.MeshBasicMaterial({ color: '#ffe9a8', transparent: true, opacity: 1 });
        const spark = new THREE.Mesh(sparkGeo, sparkMat);
        spark.position.set(entry.mesh.position.x, entry.base + 2.2 + entry.lift, entry.mesh.position.z);
        scene.add(spark);
        animsRef.current.push({
          ...next, t0: now, mesh: entry.mesh, base: entry.base, lift: entry.lift, spark, sparkMat,
        });
        combatRef.current.played += 1;
        if (next.kind === 'kill') combatRef.current.kills += 1;
      }
      // Advance.
      const live: Anim[] = [];
      for (const a of animsRef.current) {
        const p = Math.min(1, (now - a.t0) / ANIM_MS[a.kind]);
        // Ease-out so the motion snaps then settles.
        const e = 1 - Math.pow(1 - p, 3);
        if (a.kind === 'strike') {
          // Flinch: a quick swell that springs back.
          const pulse = 1 + 0.35 * Math.sin(p * Math.PI);
          a.mesh.scale.setScalar((a.base / MOB_SIZE) * pulse);
          a.mesh.position.y = (a.base * pulse) / 2 + a.lift;
          a.spark.position.y = a.base + 2.2 + a.lift + e * 3;
          a.sparkMat.opacity = 1 - e;
          a.spark.rotation.z = e * 0.9;
        } else {
          // Kill: the mob is cut down and shrinks into rubble.
          a.mesh.scale.setScalar((a.base / MOB_SIZE) * (1 - e));
          a.mesh.position.y = (a.base * (1 - e)) / 2 + a.lift;
          a.mesh.rotation.y += 0.12;
          a.spark.position.y = a.base + 2.2 + a.lift + e * 4;
          a.sparkMat.opacity = 1 - e;
          a.sparkMat.color.setStyle(p < 0.4 ? '#fff3c9' : '#ff9b52');
        }
        if (p < 1) {
          live.push(a);
        } else {
          scene.remove(a.spark);
          a.sparkMat.dispose();
          if (a.kind === 'kill') {
            // It falls, and it is gone. Remembered so a rebuild before
            // the data catches up does not put it back on its feet.
            killedLocallyRef.current.add(a.key);
            mobMeshesRef.current.delete(a.key);
            a.mesh.parent?.remove(a.mesh);
          } else {
            a.mesh.scale.setScalar(a.base / MOB_SIZE);
            a.mesh.position.y = a.mesh.userData.restY as number;
          }
        }
      }
      animsRef.current = live;
    }

    animate();

    const onResize = () => {
      if (!mount.clientWidth || !mount.clientHeight) return;
      camera.aspect = mount.clientWidth / mount.clientHeight;
      camera.updateProjectionMatrix();
      renderer.setSize(mount.clientWidth, mount.clientHeight);
    };
    window.addEventListener('resize', onResize);

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', onResize);
      renderer.domElement.removeEventListener('pointerdown', onPointerDown);
      renderer.domElement.removeEventListener('pointerup', onPointerUp);
      refreshTethersRef.current = null;
      pieceScreenRef.current = null;
      heroScreenRef.current = null;
      labelLayer.remove();
      clearTethers();
      for (const a of animsRef.current) a.sparkMat.dispose();
      animsRef.current = [];
      // The renderer, the camera and the controls are deliberately NOT
      // disposed here. They belong to the PAGE, not to the scene. See the
      // refs above: they are released on unmount, in their own effect.
    };
  }, [field, data, showGraveyard, spin, staleDays, dotCycleMs, dotPx, shadows, assigneeOf, actorsOf, heroColorById, heroNameById, botIds]);

  // Release the WebGL context, the camera and the controls when the PAGE
  // goes away, not when the scene is rebuilt.
  useEffect(
    () => () => {
      controlsRef.current?.dispose();
      controlsRef.current = null;
      cameraRef.current = null;
      rendererRef.current?.dispose();
      rendererRef.current = null;
    },
    [],
  );

  return (
    <div className="fixed inset-0 flex flex-col bg-bg text-ink">
      <header className="flex items-center gap-3 border-b border-white/10 px-6 py-3">
        <Link to="/admin" className="text-sm text-ink-muted hover:text-ink">← back</Link>
        <span className="text-lg font-semibold">Battlemap</span>
        {live.data?.adventure ? (
          <span className="text-xs text-amber-300">
            {live.data.adventure.place
              ? `The ${live.data.adventure.place} of ${live.data.adventure.name}`
              : live.data.adventure.name}
          </span>
        ) : null}
        <span className="text-xs text-ink-dim">drag to orbit · scroll to zoom</span>
        <div className="ml-auto flex items-center gap-3 text-xs">
          {(teams.data?.teams ?? []).length > 1 ? (
            <select
              className="rounded border border-border bg-bg-elevated px-1.5 py-0.5"
              value={teamId}
              onChange={(e) => setTeamId(e.target.value)}
            >
              {(teams.data?.teams ?? []).map((t) => (
                <option key={t.id} value={t.id}>{t.name}</option>
              ))}
            </select>
          ) : null}
          <label className="flex items-center gap-1.5">
            <input type="checkbox" checked={showGraveyard} onChange={(e) => setShowGraveyard(e.target.checked)} />
            graveyard
          </label>
          <label className="flex items-center gap-1.5">
            <input type="checkbox" checked={shadows} onChange={(e) => setShadows(e.target.checked)} />
            shadows
          </label>
          <label className="flex items-center gap-1.5">
            <input type="checkbox" checked={showLegend} onChange={(e) => setShowLegend(e.target.checked)} />
            legend
          </label>
          <label className="flex items-center gap-1.5">
            <input type="checkbox" checked={spin} onChange={(e) => setSpin(e.target.checked)} />
            orbit
          </label>
          <label
            className="flex items-center gap-1.5"
            title="Replay the window instead of showing now"
          >
            <input
              type="checkbox"
              checked={replayMode}
              onChange={(e) => {
                setReplayMode(e.target.checked);
                // Leaving replay must clear the playhead, or live mode
                // would still be describing a past instant.
                if (!e.target.checked) setPlayheadMs(null);
              }}
            />
            replay
          </label>
          <span className="tabular-nums text-ink-dim" title="combat animations played this session">
            ⚔ {combat.played}{combat.kills > 0 ? ` (${combat.kills} kills)` : ''}
          </span>
          <span className="tabular-nums text-ink-dim">
            {stats.fps} fps · {stats.drawCalls} calls · {stats.tris.toLocaleString()} tris
          </span>
        </div>
      </header>

      <div className="relative flex-1">
        {/* Vignette: one CSS radial gradient over the canvas, so it costs
            no draw calls and no shader. It does what a lens does — pulls
            the eye to the middle of the board and lets the corners fall
            away, which also softens the hard rectangle where the ground
            ends. */}
        <div
          className="pointer-events-none absolute inset-0 z-[5]"
          style={{
            background:
              // Was 0.30 at 78% and 0.60 at the corners. That is a lot
              // of black laid over an already-dark scene, and it was
              // taking 60% off the sky in the corners.
              'radial-gradient(ellipse at 50% 45%, rgba(0,0,0,0) 55%, rgba(0,0,0,0.16) 82%, rgba(0,0,0,0.36) 100%)',
          }}
        />
        {/* The three.js canvas mounts into its own absolutely-positioned
            layer so React overlays (the panel, the loading states) are
            siblings rather than children of the mount the effect owns. */}
        <div
          ref={mountRef}
          className="absolute inset-0"
          style={{
            // The sky. Composited by the browser, so it costs the GPU
            // nothing at all.
            //
            // Subtle on purpose, and it passes THROUGH the fog colour at
            // the band where the far edge of the board sits: the fogged
            // ground fades to #0b0d10, so a sky that differed there would
            // meet it at a visible seam. The warm lift is ABOVE that line.
            //
            // Not a true horizon — a screen-space gradient cannot stay
            // aligned to the world when the board is orbited, and a
            // world-aligned one would have to be geometry, which is the
            // fill cost that got the table dropped.
            // Brightened substantially. It was #101017 to #07080b — a
            // near-black gradient, which under a vignette read as pure
            // black and made the top half of the screen dead space.
            // The 62% stop is the horizon and matches the fog colour
            // exactly.
            background:
              'linear-gradient(to bottom, #2a3446 0%, #3b3830 44%, #2e2b26 62%, #131210 100%)',
          }}
        />
        {selectedFacts ? (
          <div className="pointer-events-auto absolute left-4 top-4 z-10 w-72 rounded-lg border border-white/10 bg-black/85 p-3 text-sm backdrop-blur">
            <div className="flex items-start justify-between gap-2">
              <span className="font-mono text-sm font-semibold text-ink">{selectedFacts.key}</span>
              <button
                type="button"
                className="shrink-0 text-[11px] text-ink-dim underline hover:text-ink"
                onClick={() => {
                  selectedMobRef.current = null;
                  setSelectedMob(null);
                }}
              >
                close
              </button>
            </div>

            {selectedFacts.summary ? (
              <p className="mt-1 text-xs leading-snug text-ink-muted">{selectedFacts.summary}</p>
            ) : null}

            <div className="mt-2 flex flex-wrap items-center gap-1.5 text-[11px]">
              {selectedFacts.status ? (
                <span className="rounded bg-white/10 px-1.5 py-0.5 text-ink">{selectedFacts.status}</span>
              ) : (
                <span className="rounded bg-white/5 px-1.5 py-0.5 text-ink-dim">no status seen</span>
              )}
              {selectedFacts.type ? (
                <span className="rounded bg-white/5 px-1.5 py-0.5 text-ink-muted">{selectedFacts.type}</span>
              ) : null}
              {selectedFacts.cleared ? (
                <span className="rounded bg-emerald-400/15 px-1.5 py-0.5 text-emerald-200">cleared</span>
              ) : null}
              {selectedFacts.stale && !selectedFacts.cleared ? (
                <span className="rounded bg-amber-400/15 px-1.5 py-0.5 text-amber-200">
                  {selectedFacts.daysSinceMove === null
                    ? 'never moved'
                    : `idle ${Math.floor(selectedFacts.daysSinceMove)}d`}
                </span>
              ) : null}
            </div>

            <dl className="mt-2 space-y-1 text-xs">
              <div className="flex justify-between gap-3">
                <dt className="text-ink-dim">Assigned</dt>
                <dd className="flex items-center gap-1.5 text-ink">
                  {selectedFacts.assigneeId ? (
                    <>
                      <span
                        className="inline-block h-2 w-2 rounded-full"
                        style={{ background: heroColorById.get(selectedFacts.assigneeId) ?? '#8b93a1' }}
                      />
                      {selectedFacts.assigneeName}
                    </>
                  ) : (
                    <span className="text-ink-dim">nobody</span>
                  )}
                </dd>
              </div>
              <div className="flex justify-between gap-3">
                <dt className="text-ink-dim">Last actor</dt>
                <dd className="flex items-center gap-1.5 text-ink-muted">
                  {selectedFacts.actorId ? (
                    <>
                      <span
                        className="inline-block h-2 w-2 rounded-full"
                        style={{ background: heroColorById.get(selectedFacts.actorId) ?? '#8b93a1' }}
                      />
                      {selectedFacts.actorIsBot ? '🤖 ' : ''}
                      {selectedFacts.actorName}
                      {selectedFacts.actorId === selectedFacts.assigneeId ? ' (assignee)' : ''}
                    </>
                  ) : (
                    <span className="text-ink-dim">unknown</span>
                  )}
                </dd>
              </div>
              {selectedFacts.goalName ? (
                <div className="flex justify-between gap-3">
                  <dt className="text-ink-dim">Goal</dt>
                  <dd className="text-ink">{selectedFacts.goalName}</dd>
                </div>
              ) : null}
              {selectedFacts.minionBossName ? (
                <div className="flex justify-between gap-3">
                  <dt className="text-ink-dim">Counts toward</dt>
                  <dd className="text-ink">{selectedFacts.minionBossName}</dd>
                </div>
              ) : null}
              <div className="flex justify-between gap-3">
                <dt className="text-ink-dim">Last moved</dt>
                <dd className="text-ink">
                  {selectedFacts.daysSinceMove === null
                    ? 'never'
                    : selectedFacts.daysSinceMove < 1
                      ? 'today'
                      : `${Math.floor(selectedFacts.daysSinceMove)}d ago`}
                </dd>
              </div>
            </dl>
          </div>
        ) : null}
        {(loading || failed || noAdventure) && (
          <div className="pointer-events-none absolute inset-0 grid place-items-center">
            <div className="pointer-events-auto max-w-md rounded-lg border border-white/10 bg-black/70 p-6 text-center text-sm">
              {loading ? <p className="text-ink-muted">Loading the battlefield…</p> : null}
              {failed ? (
                <p className="text-accent-danger">
                  Could not load the battlemap payload.
                </p>
              ) : null}
              {noAdventure ? (
                <>
                  <p className="font-semibold text-amber-300">No active adventure.</p>
                  <p className="mt-2 text-ink-muted">
                    The battlefield is a picture of the current campaign's promise, so
                    there is nothing to draw without one.
                  </p>
                </>
              ) : null}
            </div>
          </div>
        )}
      </div>

      {/* Legend + headline. The headline is the point of the whole
          view: committed work remaining vs sprint elapsed. The sprint
          clock is a number here only — it is not drawn on the
          battlefield. */}
      {/* Absolutely positioned so its gradient can fade over the
          canvas. */}
      <footer
        className="pointer-events-none absolute left-0 right-0 flex items-end justify-between gap-6 bg-gradient-to-t from-black/80 to-transparent pl-6 pr-6 pb-5 pt-16 text-sm"
        style={{ bottom: replayMode ? 116 : 0 }}
      >
        {showLegend ? (
        <div className="pointer-events-auto space-y-1 text-xs text-ink-muted">
          <Legend swatch="#e8dcbb" shape="box" label="Committed ticket" />
          <Legend swatch="#c9a14a" shape="box" label="Goal ticket" />
          <Legend swatch="#d8455a" shape="ring" label="Boss minion (ring — also in an epic)" />
          <Legend swatch="#e8dcbb" shape="box" label="Horns = status: To Do 5, In Progress 4, Review 3, Testing 2, Acceptance 1" />
          <Legend swatch="#e8dcbb" shape="box" label="Blocked and unknown statuses keep all five — stopped, not wounded" />
          <Legend swatch="#9cc2e8" shape="plat" label="Blocked — a dome over the piece" />
          <Legend swatch="#fbbf24" shape="line" label="Replay ray: status change — SOLID (the work moved)" />
          <Legend swatch="#38bdf8" shape="line" label="Replay ray: comment or PR — DOTTED (it assisted, the work stood still)" />
          <Legend swatch="#c9d1dc" shape="line" label="Assigned to (solid, held)" />
          <Legend swatch="#78818f" shape="line" label="Assisting (dots, moving)" />
          <Legend swatch="#e8721a" shape="line" label="Assigned to unplanned work" />
          <Legend swatch="#8f4a17" shape="line" label="Assisting unplanned work" />
          <Legend swatch="#e8721a" shape="box" label="Reinforcement (arrived mid-sprint, uncommitted)" />
          <Legend swatch={HERO_FALLBACK} shape="cone" label="Hero (owner of live work)" />
          <Legend swatch="#a06bff" shape="cone" label="🤖 Automation (non-human actor)" />
          <Legend swatch="#3a2f1a" shape="plat" label="Goal encampment" />
          <Legend swatch="#a83232" shape="box" label="Boss / epic (spans sprints)" />
          <Legend swatch="#4a5a3a" shape="box" label="Grave — side quest, closed, never promised" />
          <span className="pt-1 text-ink-dim">⚔ flinch on move · sink toward done · fall when closed</span>
        </div>
        ) : null}
        <div className="ml-auto text-right">
          {/* Discoverability: nothing on the board says the cones are
              clickable, so say it here. */}
          {selectedHero ? (
            <div className="mb-2 text-sm">
              <span className="font-semibold text-ink">
                {heroNameById.get(selectedHero) ?? selectedHero}
              </span>
              <span className="text-ink-muted">
                {' '}— {allPieces.filter((m) => m.ownerId === selectedHero && !m.cleared).length} live
              </span>
              <button
                type="button"
                className="ml-2 text-xs text-ink-dim underline hover:text-ink"
                onClick={() => {
                  selectedHeroRef.current = null;
                  setSelectedHero(null);
                }}
              >
                clear
              </button>
            </div>
          ) : (
            <div className="mb-2 text-xs text-ink-dim">click a hero to see what they own</div>
          )}
          <div className="text-3xl font-bold tabular-nums">
            {field.remaining}<span className="text-ink-dim">/{field.committedTotal}</span>
          </div>
          <div className="text-xs uppercase tracking-wide text-ink-muted">committed still standing</div>
          <div className="mt-2 text-lg font-semibold tabular-nums text-amber-300">
            {Math.round(field.elapsedFraction * 100)}%
          </div>
          <div className="text-xs uppercase tracking-wide text-ink-muted">sprint elapsed</div>
          <div className="mt-2 text-xs text-ink-dim">
            {(() => {
              const a = live.data?.automation;
              if (!a || a.actors === 0) return null;
              return (
                <span className="text-violet-300" title={`${a.xp} of ${a.total_xp} XP this campaign was earned by ${a.actors} non-human actor${a.actors === 1 ? '' : 's'}`}>
                  🤖 automation: {a.pct}% of campaign XP ({a.xp}/{a.total_xp}) ·{' '}
                </span>
              );
            })()}
            {staleCount > 0 ? (
              <>
                {staleCount} committed stale
                {' ('}
                <input
                  type="number"
                  min="1"
                  max="30"
                  value={staleDays}
                  onChange={(e) => {
                    const next = new URLSearchParams(searchParams);
                    const v = Number(e.target.value);
                    if (Number.isFinite(v) && v > 0) next.set('stale', String(v));
                    else next.delete('stale');
                    setSearchParams(next, { replace: true });
                  }}
                  className="w-10 rounded border border-white/15 bg-black/40 px-1 py-0 text-center"
                  title="days without a status change before a ticket counts as stale"
                />
                {'+ days quiet) · '}
              </>
            ) : null}{field.reinforcements.length} reinforcements · {field.graveyard.length} graves · {field.goals.length} encampments · {field.bosses.length} bosses
            {field.minionsStanding > 0 ? ` · ${field.minionsStanding} standing are boss minions` : ''}
          </div>
        </div>
      </footer>

      {/* Comment pings. One expanding ring per comment, on tickets that
          are still standing. DOM rather than scene objects: a ping is
          transient, must survive a rebuild mid-flight, and a CSS keyframe
          animates it for nothing. */}
      {/* Attribution rays. A short line from whoever caused the event to
          the ticket it happened to, drawn JUST BEFORE the indicator so the
          eye reads "this person, this ticket" and only then "something
          happened". Without it the board says what changed but never who
          changed it, which is the first question anyone asks. */}
      {pings.some((p) => p.from) ? (
        <svg className="pointer-events-none fixed inset-0 z-20" width="100%" height="100%">
          {pings.map((p) =>
            p.from ? (
              <line
                key={p.id}
                x1={p.from.x} y1={p.from.y} x2={p.x} y2={p.y}
                pathLength={1}
                className={`ping-ray ${isAlarm(p) ? 'ping-ray-back' : `ping-ray-${p.kind}`}`}
              />
            ) : null,
          )}
        </svg>
      ) : null}

      {/* A red exclamation over the shoulder of whoever is ACCOUNTABLE,
          thrown when their ticket goes backwards. Deliberately a second
          element rather than a restyle of the mark: the mark says what
          happened to the work, this says whose problem it is, and they are
          at different ends of the line. */}
      {pings.map((p) =>
        isAlarm(p) && p.from ? (
          <span
            key={`bang-${p.id}`}
            className="ping-bang pointer-events-none fixed z-30 select-none font-bold text-[#ff4d4f]"
            style={{ left: p.from.x + 12, top: p.from.y - 44 }}
            title={p.arrived ? `${p.key} · NEW TO THE FIELD` : p.key}
          >
            !
          </span>
        ) : null,
      )}

      {pings.map((p) => (
        <span
          key={p.id}
          title={`${p.key} · ${p.kind}${p.backwards ? ' · BACKWARDS' : ''}${p.arrived ? ' · ARRIVED' : ''}`}
          className={`pointer-events-none fixed z-30 block ping-${isAlarm(p) ? 'back' : p.kind}`}
          style={
            p.kind === 'transition'
              // A filled chevron, rising. Movement is the piece itself
              // changing, so the mark points the way the board moved it.
              ? { left: p.x - 9, top: p.y - 18, width: 18, height: 14, borderRadius: 2 }
              // A ring for a comment: an aside laid on the piece, not a
              // change to it. And a diamond for a PR, which is a different
              // KIND of thing happening to the same ticket — code moved,
              // not the ticket.
              : p.kind === 'pr'
                ? { left: p.x - 11, top: p.y - 11, width: 22, height: 22, borderWidth: 2 }
                : { left: p.x - 16, top: p.y - 16, width: 32, height: 32, borderWidth: 2 }
          }
        />
      ))}

      {replayMode ? (
        <div className="pointer-events-auto absolute bottom-0 left-0 right-0 z-20 border-t border-white/10 bg-black/80 backdrop-blur-sm">
          {timeline.isLoading ? (
            <div className="px-4 py-6 text-xs text-ink-dim">Loading the timeline…</div>
          ) : timeline.isError ? (
            <div className="px-4 py-6 text-xs text-accent-danger">
              Could not load the replay timeline.
            </div>
          ) : buckets.length > 0 && playheadMs !== null ? (
            <ReplayRail
              playing={playing}
              speed={speed}
              onTogglePlay={() => {
                if (playing) {
                  setPlaying(false);
                  return;
                }
                // Pressing play at the end would do nothing at all — the
                // playhead is already past the last event — and a button
                // that silently does nothing reads as broken. Wind back to
                // the start instead, which is what "play it again" means.
                const atEnd = !nextInstant(instants, playheadMs);
                const start = atEnd ? (instants[0] ?? replayWindow!.from) : playheadMs;
                if (atEnd) setPlayheadMs(start);
                // Seed the loop's own position, so it resumes from HERE
                // rather than from whatever it held when it last stopped.
                playRef.current.index = Math.max(0, indexAt(instants, start) - 1);
                playRef.current.carry = 0;
                setPlaying(true);
              }}
              onSpeed={setSpeed}
              onPrev={() => setPlayheadMs(prevInstant(instants, playheadMs) ?? replayWindow!.from)}
              onNext={() => {
                const n = nextInstant(instants, playheadMs);
                firePings(playheadMs, n ?? replayWindow!.to);
                setPlayheadMs(n ?? playheadMs);
              }}
              onFirst={() => { setPlaying(false); setPlayheadMs(instants[0] ?? replayWindow!.from); }}
              onLast={() => { setPlaying(false); setPlayheadMs(instants[instants.length - 1] ?? replayWindow!.to); }}
              buckets={buckets}
              gaps={gaps}
              fromMs={replayWindow!.from}
              toMs={replayWindow!.to}
              playheadMs={playheadMs}
              onScrub={(ms) => { setPlaying(false); setPlayheadMs(ms); }}
              eventsBefore={instants.filter((i) => i <= playheadMs).length}
              eventCount={instants.length}
              formatInstant={fmtInstant}
              formatDay={fmtDay}
            />
          ) : timeline.data && timeline.data.adventure === null ? (
            <div className="px-4 py-6 text-xs text-ink-dim">
              No active campaign, so there is nothing to replay.
            </div>
          ) : (
            <div className="px-4 py-6 text-xs text-ink-dim">
              No events in this campaign yet.
            </div>
          )}
        </div>
      ) : null}
    </div>
  );
}

function Legend({
  swatch,
  shape,
  label,
}: {
  swatch: string;
  shape: 'box' | 'cone' | 'plat' | 'line' | 'ring';
  label: string;
}) {
  return (
    <div className="flex items-center gap-2">
      <span
        className={
          shape === 'cone'
            ? 'inline-block h-0 w-0 border-x-[6px] border-b-[12px] border-x-transparent'
            : shape === 'line'
              ? 'h-0 w-4 border-t-2 border-dashed'
              : shape === 'plat'
                ? 'h-2.5 w-4 rounded-sm'
                : shape === 'ring'
                  ? 'h-3 w-3 rounded-full border-2'
                  : 'h-3 w-3'
        }
        style={
          shape === 'cone'
            ? { borderBottomColor: swatch }
            : shape === 'ring'
              ? { borderColor: swatch }
              : shape === 'line'
                ? { borderTopColor: swatch }
                : { background: swatch }
        }
      />
      {label}
    </div>
  );
}
